import '@angular/localize';
import { v4 as uuid } from 'uuid';
import jsonata from 'jsonata';

/**
 * Represents an uploaded or recorded file attached to a chat message.
 */
export interface FileAttachment {
  id: string;
  name: string;
  size: number;
  mimeType: string;
  url?: string;
  file?: File;
  isProcessing?: boolean;
  extension?: string;
}

/**
 * Represents a Direct Message thread between the User and the AI.
 */
export interface ChatThread {
  id: string;
  title: string;
  channelId?: string;
  createdDate: Date;
  status?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

/**
 * Represents the configuration for an HTTP endpoint action payload.
 */
export interface AiActionHttpPayload {
  method: string;
  path: string;
  proxyPath?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  body?: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  params?: any;
  returnType: string;
  responseMessages: {
    success: string;
    error: string;
  };
  needsConfirmation: boolean;
}

/**
 * Tracks the live execution status of an API action triggered from the chat.
 */
export interface AiActionExecutionState {
  status: 'idle' | 'loading' | 'success' | 'error';
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  response?: any;
  error?: string;
  correlationId?: string; // Tracks the background workflow ID if the action triggers an async orchestration
}

/**
 * Represents a single reasoning step or thought process from the AI.
 */
/**
 * One Company Knowledge note the runtime saved during the turn (`__rapider.memoryUpdates`).
 */
export interface MemoryUpdate {
  /** Memory operation (`add`, `reinforce`, `supersede`, `conflict`, `link`). */
  op: string;
  /** Page key of the note. */
  pageKey: string;
  /** Notebook (domain) key. */
  notebookKey: string;
  /** `company` or `user` (the person's private knowledge). */
  scope: string;
  /** Note title. */
  title: string;
  /** One-sentence statement. */
  statement: string;
  /** Lifecycle status (e.g. `active`, `tentative`). */
  status: string;
}

export interface ReasoningStep {
  id?: string;
  title?: string;
  content: string;
  status?: 'pending' | 'active' | 'completed' | 'failed' | string;
  /** Optional secondary subtitle or description */
  subtitle?: string;
  /** Backend capability or tool associated with this step */
  capabilityName?: string;
  /** Async workflow or tool execution correlation ID */
  correlationId?: string;
  /** Event type name from ActivityStream */
  eventType?: string;
  /** Structured data or parameter payload */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data?: any;
  /** Raw payload object */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  payload?: any;
}

/**
 * Represents a single option within an interaction request.
 */
export interface InteractionOption {
  title: string;
  value: string;
}

/**
 * Configuration for an interactive user prompt (e.g., radio buttons, checkboxes).
 */
export interface InteractionConfig {
  type: 'radio' | 'multiSelect';
  options: InteractionOption[];
}

/**
 * Represents the local state tracking for an interaction widget.
 */
export interface InteractionState {
  status: 'pending' | 'responded' | 'skipped';
  selectedValues: string[];
}

/**
 * Represents a backend AI tool execution (e.g., calling an external API or database).
 */
export interface AiToolCall {
  id?: string;
  name: string;
  title?: string;
  description?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  arguments: any;
  status?: 'pending' | 'success' | 'error';
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  result?: any;
  error?: string | null;
}

/**
 * Represents an individual step within an asynchronous progress task.
 */
export interface AsyncProgressStep {
  title: string;
  status: 'pending' | 'active' | 'completed' | 'failed' | 'error';
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  result?: any;
  resultMarkdown?: string;
  isExpanded?: boolean; // Used by UI to toggle response visibility
}

/**
 * Represents the live polling progress of a long-running backend process.
 */
export interface AsyncProgress {
  percentage: number;
  statusText: string;
  steps?: AsyncProgressStep[];
  isPolling?: boolean;
  correlationId?: string; // Tracks the background orchestrator ID for polling
  resultMarkdown?: string;
}

/**
 * Represents a single task generated by the Workflow Orchestrator Agent.
 */
export interface WorkflowTask {
  type: string;
  name?: string;
  description: string;
  status: 'pending' | 'in-progress' | 'completed' | 'failed' | 'error';
  expectedOutputSchema?: any; // Defines the expected JSON schema structure for the task's output, used on transformation steps
}

/**
 * Represents an isolated update instruction for a workflow plan step.
 */
export interface WorkflowPlanUpdate {
  originatingStepId: string;
  stepIndex: number;
  status: 'pending' | 'in-progress' | 'completed' | 'failed' | 'error';
}

/**
 * Represents the declarative workflow plan generated by the AI Orchestrator.
 */
export interface WorkflowPlan {
  originalMessageId?: string; // Tracks the ID of the message that spawned this plan
  tasks: WorkflowTask[];
  executionState?: AiActionExecutionState; // Tracks execution of the overall plan
}

/**
 * Represents a dynamic action suggested by the AI.
 */
export interface AiAction {
  operation: string;
  type: 'endpoint' | 'server_action' | string;
  execution?: 'server' | 'client' | string;
  title?: string;
  description: string;
  runDraftId?: string;
  workflowContextVariable?: string; // Optional variable name to store the action result in the workflow context, we will use the context variables to render next steps by handlebars before executing the action
  payload: {
    http?: AiActionHttpPayload;
    input?: Record<string, any>;
    capabilityKey?: string;
    runDraftId?: string;
    sourceType?: string;
    needsConfirmation?: boolean;
  };
  executionState?: AiActionExecutionState; // Tracks the API call status natively
  /**
   * Whether the user kept this action switched on in a multi-action batch (mirrors the mobile
   * `SuggestedAction.isActive`). Absent means active. A switched-off server action is reported to
   * the agent as declined (`confirmed: false`); a switched-off endpoint action is not executed.
   */
  isActive?: boolean;
}

/**
 * Represents the UI configuration mapping for the actions.
 */
export interface AiActionsUIConfig {
  operationButtons?: Record<
    string,
    { primaryText: string; secondaryText: string }
  >;
}

export interface TraceTask {
  id: string;
  name: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  interrupts?: any[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  result?: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  error?: any;
  aiLogId?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  aiLogData?: any;
  isAiLogLoading?: boolean;
  isExpanded?: boolean;
}

export interface TraceNode {
  checkpoint_id: string;
  step: number;
  source: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  writes?: Record<string, any>;
  createdAt: string;
  state: any;
  name?: string;
  tasks?: TraceTask[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  events?: any[];

  // UI-specific properties (calculated on frontend)
  nodeName?: string;
  durationMs?: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  stateIterative?: {
    key: string;
    type: 'added' | 'deleted' | 'updated' | 'unchanged';
    value: any;
  }[];
  isExpanded?: boolean;
  isEventsExpanded?: boolean;
  isStateExpanded?: boolean;
  isMaxDuration?: boolean;
  durationPercentage?: number;
}

/**
 * Represents a single message within a chat thread.
 */
export interface AIChatMessage {
  id: string;
  text: string;
  isUser: boolean;
  timestamp: Date;
  type?: string; // Tracks if the message is a 'workflow-step' or standard text
  isConfirmation?: boolean; // Tracks if this message serves as a user confirmation flag
  isShownInChat?: boolean; // Tracks whether the message should be rendered in the visible chat timeline
  isHidden?: boolean; // Alternative flag to suppress rendering of internal/system utility messages
  /** Tracks whether this message is actively streaming tokens, activity events, or waiting for execution */
  isStreaming?: boolean;
  /** Tracks whether the message entrance and typewriter animation has completed */
  hasBeenAnimated?: boolean;
  status?: string; // Tracks the backend status (e.g., 'sent')
  aiFunctionId?: string; // Tracks which agent the step is routed to
  attachments?: FileAttachment[];
  isVoiceMessage?: boolean;
  voiceUrl?: string;
  voiceFileId?: string; // Stores the backend reference ID to refetch the audio blob
  isError?: boolean;
  originatingRequestMessageId?: string; // Tracks the ID of the user's original request message
  reasoningText?: string; // Tracks raw text reasoning
  reasoningSteps?: ReasoningStep[]; // Tracks structured step-by-step reasoning
  feedback?: 'like' | 'dislike'; // Tracks the user's feedback for the message
  transcribedMessage?: string; // Tracks the transcribed text from a voice message explicitly
  interactionConfig?: InteractionConfig; // Configuration for structured user inputs
  interactionState?: InteractionState; // Local tracking of the user's response to this interaction
  toolCalls?: AiToolCall[]; // Tracks agentic tool calls executed by the AI
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  rawRapiderPayload?: any; // Stores the raw __rapider payload for debug trace visualization
  asyncProgress?: AsyncProgress; // Tracks progress of long-running background tasks
  memoryUpdates?: MemoryUpdate[]; // Company Knowledge notes saved during this turn
  actionResponseData?: {
    actionResponseStatus: 'success' | 'error';
    originatingRequestMessageId?: string;
    originatedActionMessageId?: string;
    actionIndex?: number;
    entityName?: string;
    correlationId?: string;
    actionType?: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    response?: any;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    actionResults?: any[];
  };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data?: Record<string, any>;
  entityData?: {
    entityName: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data: any[];
  };
  actions?: AiAction[];
  actionsUIConfig?: AiActionsUIConfig;
  workflowPlan?: WorkflowPlan;
  workflowPlanUpdate?: WorkflowPlanUpdate; // Transient field carrying the update instructions
  workflowStepData?: { stepIndex: number; description?: string }; // Tracks the index and clean description of the step for UI
  jsonataTransformation?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  displayTemplate?: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  template?: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  displayComponent?: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  transformationResult?: any;
}

export const DEFAULT_AGENT_NAME = 'Rapider1.2';

/**
 * Global configuration object holding constants and localized text resources for the AI Chat.
 */
export const AI_CHAT_CONFIG = {
  constants: {
    // Feature toggle for Chat V2 modern mobile redesign (active on feat/ai-chat-v2-mobile-redesign)
    useV2: true,
    // Standard ID for the AI Assistant (acts as the receiver for Direct Messages)
    aiReceiverId: '00000000-0000-0000-0000-000000000001',
    // Fallback title for newly created threads without context
    defaultThreadTitle: 'New Conversation',
    // Number of threads to fetch per pagination request
    threadsPageSize: 5,
    // The default system router agent prepended to the dynamic functions list
    defaultAgentFunction: {
      id: 'router-agent',
      name: 'router-agent',
      title: DEFAULT_AGENT_NAME,
    },
    // Window event announcing a Company Knowledge change; MUST equal
    // KNOWLEDGE_SYNC.changedEventName in libs/knowledge (the libs do not import each other).
    knowledgeChangedEvent: 'rapider-knowledge-changed',
    // Knowledge notes route and its query parameters (see KNOWLEDGE_ROUTES in libs/knowledge)
    knowledgeNotesRoute: ['/knowledge', 'notes'],
    knowledgeNoteQueryParam: 'note',
    knowledgePrivateQueryParam: 'isPrivate',
    knowledgePrivateQueryValue: 'true',
    knowledgePrivateScope: 'user',
    // Redirection rules to dynamically replace API paths before execution or display
    apiPathRedirections: [
      {
        pathPart: '/ai-functions/execute-function-tools-call/',
        replaceWith: '/ai-function-calls/execute-function-tools-call/',
      },
    ],
    // Fields to project when listing chat threads to optimize API payload size and fetch time
    threadListFields: {
      createdDate: true,
      id: true,
      title: true,
      status: true,
    },
  },
  texts: {
    inputPlaceholder: $localize`:@@aiChat.inputPlaceholder:Type your message or click the microphone to record... (Opt + F to focus)`,
    sendButton: $localize`:@@aiChat.sendButton:Send Message`,
    recordButton: $localize`:@@aiChat.recordButton:Record Voice Message`,
    stopRecordButton: $localize`:@@aiChat.stopRecordButton:Stop & Send`,
    cancelRecordButton: $localize`:@@aiChat.cancelRecordButton:Cancel`,
    attachButton: $localize`:@@aiChat.attachButton:Attach Files`,
    toolsButton: $localize`:@@aiChat.toolsButton:Tools`,
    proButton: $localize`:@@aiChat.proButton:${DEFAULT_AGENT_NAME}`,
    recordingActive: $localize`:@@aiChat.recordingActive:Recording in progress...`,
    micPermissionDenied: $localize`:@@aiChat.micPermissionDenied:Microphone access was denied. Please allow permissions in your browser.`,
    micError: $localize`:@@aiChat.micError:An error occurred while accessing the microphone.`,
    maxFilesReached: $localize`:@@aiChat.maxFilesReached:You can only attach a maximum of 5 files.`,
    fileTooLarge: $localize`:@@aiChat.fileTooLarge:File size exceeds the 10MB limit.`,
    unsupportedFileType: $localize`:@@aiChat.unsupportedFileType:Unsupported file type.`,
    emptyChatTitle: $localize`:@@aiChat.emptyChatTitle:How can I help you today?`,
    memorySavedLabel: $localize`:@@aiChat.memorySavedLabel:Saved to Notebooks`,
    memorySavedPrivateLabel: $localize`:@@aiChat.memorySavedPrivateLabel:Saved to my notebooks`,
    emptyChatSubtitle: $localize`:@@aiChat.emptyChatSubtitle:Ask a question or upload a file to get started.`,
    defaultErrorMessage: $localize`:@@aiChat.defaultErrorMessage:Sorry, I encountered an error while processing your request.`,
    threadsTitle: $localize`:@@aiChat.threadsTitle:AI Threads`,
    newChatButton: $localize`:@@aiChat.newChatButton:New Chat`,
    newThreadTooltip: $localize`:@@aiChat.newThreadTooltip:New Thread`,
    loadMoreButton: $localize`:@@aiChat.loadMoreButton:Load More`,
    transcriptionLabel: $localize`:@@aiChat.transcriptionLabel:Transcription:`,
    voiceExpiredError: $localize`:@@aiChat.voiceExpiredError:Voice message expired or not found. Upgrade to Enterprise tier for permanent voice storage.`,
    voiceMessageLabel: $localize`:@@aiChat.voiceMessageLabel:Voice Message`,
    dataResultsLabel: $localize`:@@aiChat.dataResultsLabel:Data Results:`,
    // Message Action Texts
    copyAction: $localize`:@@aiChat.copyAction:Copy`,
    copiedAction: $localize`:@@aiChat.copiedAction:Copied!`,
    copyTranscriptAction: $localize`:@@aiChat.copyTranscriptAction:Copy Transcript`,
    reasoningTitle: $localize`:@@aiChat.reasoningTitle:Thinking process`,
    closePreviewBtn: $localize`:@@aiChat.closePreviewBtn:Close`,
    downloadBtn: $localize`:@@aiChat.downloadBtn:Download`,
    interactionSkipBtn: $localize`:@@aiChat.interactionSkipBtn:Skip`,
    interactionSubmitBtn: $localize`:@@aiChat.interactionSubmitBtn:Submit`,
    interactionSubmitted: $localize`:@@aiChat.interactionSubmitted:Submitted`,
    // Fallback texts for dynamically generated action buttons
    defaultActionPrimary: $localize`:@@aiChat.actionPrimary:Execute`,
    defaultActionSecondary: $localize`:@@aiChat.actionSecondary:Discard`,
    confirmActionPrimary: $localize`:@@aiChat.confirmActionPrimary:Confirm`,
    cancelActionSecondary: $localize`:@@aiChat.cancelActionSecondary:Cancel`,
    actionConfirmMessage: $localize`:@@aiChat.actionConfirmMessage:Are you sure you want to execute this action?`,
    executeAllActions: $localize`:@@aiChat.executeAllActions:Execute All Actions`,
    showInCanvasButton: $localize`:@@aiChat.showInCanvasButton:Show in canvas`,
    showAllButton: $localize`:@@aiChat.showAllButton:Show All`,
    gridViewLabel: $localize`:@@aiChat.gridViewLabel:Grid View`,
    listViewLabel: $localize`:@@aiChat.listViewLabel:List View`,
    // Data cards
    dataCardViewLabel: $localize`:@@aiChat.dataCardViewLabel:Data Card View`,
    tableViewLabel: $localize`:@@aiChat.tableViewLabel:Table View`,

    // Workflow Plan
    workflowPlanTitle: $localize`:@@aiChat.workflowPlanTitle:Execution Plan`,
    executePlanButton: $localize`:@@aiChat.executePlanButton:Execute Plan`,
    workflowTaskQuery: $localize`:@@aiChat.workflowTaskQuery:Data Query`,
    workflowTaskTransform: $localize`:@@aiChat.workflowTaskTransform:Data Transformation`,
    workflowTaskDisplay: $localize`:@@aiChat.workflowTaskDisplay:Display Results`,
    workflowTaskQuestion: $localize`:@@aiChat.workflowTaskQuestion:General Question`,
    workflowTaskSelect: $localize`:@@aiChat.workflowTaskSelect:User Selection`,
    workflowTaskGeneric: $localize`:@@aiChat.workflowTaskGeneric:Task`,
    jsonataError: $localize`:@@aiChat.jsonataError:AI did not return a valid JSONata expression codeblock.`,

    runDevAgentBtn: $localize`:@@endpointActionCard.runDevAgent:Run Rapider Dev Agent`,
    devAgentRequiredTitle: $localize`:@@endpointActionCard.devAgentRequiredTitle:Server Deployment Required`,
    devAgentRequiredMessage: $localize`:@@endpointActionCard.devAgentRequiredMessage:It looks like your server is either not built and deployed yet, or a previous deployment encountered errors. Let's try to deploy your server with the Rapider AI Dev Agent. Please click the button below to build and deploy your server, and then try this action again when your server is up and running.`,
  },
};

// --- Pure Functions for Formatting & Logic ---

/**
 * PURE FUNCTION: Extracts the `workflowPlan` array securely from the nested Rapider API response.
 * Accounts for potential typos in the AI payload like 'workFlowPlan' vs 'workflowPlan'.
 * @param {any} apiMessage - The raw message object from the backend.
 * @returns {WorkflowPlan | undefined} The extracted workflow plan or undefined.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function extractWorkflowPlan(apiMessage: any): WorkflowPlan | undefined {
  if (!apiMessage) return undefined;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const findRapiderData = (obj: any): any => {
    if (!obj) return null;
    let dataObj = obj.data;
    if (typeof dataObj === 'string') {
      try {
        dataObj = JSON.parse(dataObj);
      } catch (e) {
        /* silent fail */
      }
    }
    if (dataObj?.__rapider) return dataObj.__rapider;
    return null;
  };

  const rapiderObj =
    findRapiderData(apiMessage) ||
    findRapiderData(apiMessage.aiMessage) ||
    findRapiderData(apiMessage.message);

  // Support both camelCase variations that the AI might generate natively
  const plan = rapiderObj?.workflowPlan || rapiderObj?.workFlowPlan;

  if (plan && Array.isArray(plan.tasks)) {
    return plan;
  }
  return undefined;
}

/**
 * PURE FUNCTION: Extracts a `workflowPlanUpdate` payload securely from the nested Rapider API response.
 * @param {any} apiMessage - The raw message object from the backend.
 * @returns {WorkflowPlanUpdate | undefined} The extracted update instruction or undefined.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function extractWorkflowPlanUpdate(
  apiMessage: any,
): WorkflowPlanUpdate | undefined {
  if (!apiMessage) return undefined;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const findRapiderData = (obj: any): any => {
    if (!obj) return null;
    let dataObj = obj.data;
    if (typeof dataObj === 'string') {
      try {
        dataObj = JSON.parse(dataObj);
      } catch (e) {
        /* silent fail */
      }
    }
    if (dataObj?.__rapider) return dataObj.__rapider;
    return null;
  };

  const rapiderObj =
    findRapiderData(apiMessage) ||
    findRapiderData(apiMessage.aiMessage) ||
    findRapiderData(apiMessage.message);
  const plan = rapiderObj?.workflowPlan || rapiderObj?.workFlowPlan;

  if (
    plan &&
    plan.update &&
    (plan.update.originatingStepId || plan.update.originalMessageId)
  ) {
    return {
      originatingStepId:
        plan.update.originatingStepId || plan.update.originalMessageId,
      stepIndex:
        plan.update.stepIndex !== undefined
          ? plan.update.stepIndex
          : plan.update.taskIndex,
      status: plan.update.status,
    };
  }

  return undefined;
}

/**
 * PURE FUNCTION: Safely parses raw message data if stringified or returns the object directly.
 *
 * @param {any} apiMessage - The raw message object from the backend.
 * @returns {Record<string, any> | undefined} Parsed message data object or undefined.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function parseApiMessageData(
  apiMessage: any,
): Record<string, any> | undefined {
  if (!apiMessage) return undefined;
  let dataObj = apiMessage.data ?? apiMessage.message?.data;
  if (typeof dataObj === 'string') {
    try {
      dataObj = JSON.parse(dataObj);
    } catch {
      return undefined;
    }
  }
  return dataObj && typeof dataObj === 'object' ? dataObj : undefined;
}

/**
 * PURE FUNCTION: Safely extracts and normalizes action response data and action execution results
 * from an API message payload, regardless of message type.
 * Supports single action result objects and multi-action result arrays (`data.actionResult`).
 *
 * @param {any} apiMessage - The raw API message object from the backend.
 * @returns {AIChatMessage['actionResponseData'] | undefined} The normalized action response data, or undefined.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function extractActionResponseData(
  apiMessage: any,
): AIChatMessage['actionResponseData'] | undefined {
  if (!apiMessage) return undefined;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const dataObj: any = parseApiMessageData(apiMessage);
  if (!dataObj) return undefined;

  // Case 1: Explicit actionResponseStatus
  if (dataObj.actionResponseStatus) {
    return {
      actionResponseStatus:
        dataObj.actionResponseStatus === 'error' ? 'error' : 'success',
      originatingRequestMessageId:
        dataObj.originatingRequestMessageId || dataObj.originatingActionId,
      originatedActionMessageId: dataObj.originatedActionMessageId,
      actionIndex:
        typeof dataObj.actionIndex === 'number' ? dataObj.actionIndex : 0,
      entityName: dataObj.entityName,
      correlationId: dataObj.correlationId,
      actionType: dataObj.actionType,
      response: dataObj.response,
      actionResults: Array.isArray(dataObj.actionResult)
        ? dataObj.actionResult
        : undefined,
    };
  }

  // Case 2: Array of actionResult or single actionResult object
  const rawResults = dataObj.actionResult || dataObj.actionResults;
  if (rawResults) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const resultsArray = Array.isArray(rawResults) ? rawResults : [rawResults];
    if (resultsArray.length > 0) {
      const resolvedIndex =
        typeof dataObj.actionIndex === 'number' ? dataObj.actionIndex : 0;
      const primaryResult =
        resultsArray[resolvedIndex] ||
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        resultsArray.find((r: any) => Boolean(r)) ||
        resultsArray[0];

      const status =
        primaryResult?.status === 'error' || primaryResult?.status === 'failed'
          ? 'error'
          : 'success';

      return {
        actionResponseStatus: status,
        originatingRequestMessageId:
          dataObj.originatingRequestMessageId || dataObj.originatingActionId,
        originatedActionMessageId: dataObj.originatedActionMessageId,
        actionIndex: resolvedIndex,
        entityName: dataObj.entityName || primaryResult?.entityName,
        correlationId: dataObj.correlationId || primaryResult?.correlationId,
        actionType: dataObj.actionType,
        response:
          primaryResult?.result !== undefined
            ? primaryResult.result
            : primaryResult,
        actionResults: resultsArray,
      };
    }
  }

  return undefined;
}

/**
 * PURE FUNCTION: Extracts workflow step metadata natively from the API response payload.
 * @param {any} apiMessage - The raw message object from the backend.
 * @returns {{ stepIndex: number } | undefined} The extracted step data or undefined.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function extractWorkflowStepData(
  apiMessage: any,
): { stepIndex: number; description?: string } | undefined {
  if (!apiMessage) return undefined;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const findRapiderData = (obj: any): any => {
    if (!obj) return null;
    let dataObj = obj.data;
    if (typeof dataObj === 'string') {
      try {
        dataObj = JSON.parse(dataObj);
      } catch (e) {
        /* silent fail */
      }
    }
    if (dataObj?.__rapider) return dataObj.__rapider;
    return null;
  };
  const rapiderObj =
    findRapiderData(apiMessage) ||
    findRapiderData(apiMessage.aiMessage) ||
    findRapiderData(apiMessage.message);
  return rapiderObj?.workflowStepData;
}

/**
 * PURE FUNCTION: Extracts a `context` object securely from the nested Rapider API response.
 * Used to merge global context variables directly dictated by the orchestrator or AI agents.
 * @param {any} apiMessage - The raw message object from the backend.
 * @returns {Record<string, any> | undefined} The extracted context or undefined.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function extractContextFromAiResponse(
  apiMessage: any,
): Record<string, any> | undefined {
  if (!apiMessage) return undefined;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const findRapiderData = (obj: any): any => {
    if (!obj) return null;
    let dataObj = obj.data;
    if (typeof dataObj === 'string') {
      try {
        dataObj = JSON.parse(dataObj);
      } catch (e) {
        /* silent fail */
      }
    }
    if (dataObj?.__rapider) return dataObj.__rapider;
    return null;
  };

  const rapiderObj =
    findRapiderData(apiMessage) ||
    findRapiderData(apiMessage.aiMessage) ||
    findRapiderData(apiMessage.message);
  return rapiderObj?.context;
}

/**
 * PURE FUNCTION: Extracts an error message from the nested Rapider API response if the AI agent failed internally.
 * Frequently, AI timeouts return HTTP 200 but nest the failure inside a stringified 'error' object.
 * @param {any} apiMessage - The raw message object from the backend.
 * @returns {string | undefined} The extracted and parsed error message or undefined.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function extractAiError(apiMessage: any): string | undefined {
  if (!apiMessage) return undefined;

  // Deep search utility to safely navigate and parse stringified LoopBack JSON columns
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const findError = (obj: any): any => {
    if (!obj) return null;
    let dataObj = obj.data;
    if (typeof dataObj === 'string') {
      try {
        dataObj = JSON.parse(dataObj);
      } catch (e) {
        /* silent fail */
      }
    }
    if (dataObj?.error) return dataObj.error;
    if (dataObj?.__rapider?.error) return dataObj.__rapider.error;
    return obj.error;
  };

  const rawError =
    findError(apiMessage.aiMessage) ||
    findError(apiMessage.message) ||
    findError(apiMessage);

  if (rawError) {
    if (typeof rawError === 'string') {
      try {
        // AI errors are often double-stringified JSON (e.g., "{\"message\":\"deadline expired\"}")
        const parsedError = JSON.parse(rawError);
        return parsedError.message || parsedError.error || rawError;
      } catch {
        return rawError; // Fallback to the raw string if parsing fails
      }
    }
    // If it's an object, try to extract standard error properties
    return rawError.message || rawError.error || JSON.stringify(rawError);
  }

  return undefined;
}

/**
 * PURE FUNCTION: Generates a sample context by heavily truncating all arrays to a maximum of 1 item.
 * Preserves the object structure while drastically reducing token payload size for AI consumption.
 * @param {any} obj - The context object to sample.
 * @returns {any} A heavily truncated clone of the context.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function generateSampleContext(obj: any): any {
  if (obj === null || obj === undefined) return obj;

  if (Array.isArray(obj)) {
    if (obj.length === 0) return [];
    // Only keep the first item to sample the array structure
    return [generateSampleContext(obj[0])];
  }

  if (typeof obj === 'object') {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const sampled: any = {};
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        sampled[key] = generateSampleContext(obj[key]);
      }
    }
    return sampled;
  }

  // Return primitive values as they are
  return obj;
}

/**
 * PURE FUNCTION: Builds a strict instruction prompt for the JSONata Transformation agent.
 * Injects the task description, context variables descriptions, the truncated sample data context, and optionally the expected output schema.
 * @param {string} taskDescription - The human-readable description of the transformation logic.
 * @param {Record<string, any>} sampleContext - The truncated JSON data representing available variables.
 * @param {Record<string, string>} contextDescriptions - The mapping of variable names to their endpoint explanations.
 * @param {any} expectedSchema - Optional JSON schema that the next step expects as input.
 * @returns {string} The fully compiled system prompt for the transformation agent.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function buildJsonataTransformationPrompt(
  taskDescription: string,
  sampleContext: Record<string, any>,
  contextDescriptions: Record<string, string> = {},
  expectedSchema?: any,
): string {
  let prompt = `Task: ${taskDescription}\n\n`;

  // Provide the AI with explicit explanations of what data each variable holds based on the endpoint descriptions
  if (contextDescriptions && Object.keys(contextDescriptions).length > 0) {
    prompt += `Context Data Variables Explanation:\n This is how we have fetched data from the endpoints, and what each variable in the context represents:\n`;
    for (const [key, desc] of Object.entries(contextDescriptions)) {
      prompt += `- ${key}: ${desc}\n`;
    }
    prompt += `\n`;
  }

  // Strip internal metadata from the sample context before passing to AI to avoid confusion
  const cleanSampleContext = { ...sampleContext };
  delete cleanSampleContext['__metadata'];

  prompt += `Below is a sample of the current data context available for transformation. Use this to construct your JSONata expression:\n`;
  prompt += `\`\`\`json\n${JSON.stringify(cleanSampleContext, null, 2)}\n\`\`\`\n\n`;

  if (expectedSchema) {
    prompt += `The expected output structure MUST conform to this schema:\n`;
    prompt += `\`\`\`json\n${JSON.stringify(expectedSchema, null, 2)}\n\`\`\`\n\n`;
  }

  prompt += `You must return ONLY a valid JSONata expression. Do not wrap it with other markdown etc, and do not include any other explanations.`;
  return prompt;
}

/**
 * PURE FUNCTION: Extracts a display template securely from the nested Rapider API response.
 * @param {any} apiMessage - The raw message object from the backend API.
 * @returns {any | undefined} The extracted template object, or undefined.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function extractDisplayTemplate(apiMessage: any): any | undefined {
  if (!apiMessage) return undefined;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const findRapiderData = (obj: any): any => {
    if (!obj) return null;
    let dataObj = obj.data;
    if (typeof dataObj === 'string') {
      try {
        dataObj = JSON.parse(dataObj);
      } catch (e) {
        /* silent fail */
      }
    }
    if (dataObj?.__rapider) return dataObj.__rapider;
    return null;
  };
  const rapiderObj =
    findRapiderData(apiMessage) ||
    findRapiderData(apiMessage.aiMessage) ||
    findRapiderData(apiMessage.message);
  return rapiderObj?.displayTemplate;
}

/**
 * PURE FUNCTION: Extracts a generic template securely from the nested Rapider API response.
 * @param {any} apiMessage - The raw message object from the backend API.
 * @returns {any | undefined} The extracted template object, or undefined.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function extractTemplate(apiMessage: any): any | undefined {
  if (!apiMessage) return undefined;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const findRapiderData = (obj: any): any => {
    if (!obj) return null;
    let dataObj = obj.data;
    if (typeof dataObj === 'string') {
      try {
        dataObj = JSON.parse(dataObj);
      } catch (e) {
        /* silent fail */
      }
    }
    if (dataObj?.__rapider) return dataObj.__rapider;
    return null;
  };
  const rapiderObj =
    findRapiderData(apiMessage) ||
    findRapiderData(apiMessage.aiMessage) ||
    findRapiderData(apiMessage.message);
  return rapiderObj?.template || rapiderObj?.displayTemplate;
}

/**
 * PURE FUNCTION: Extracts a dynamic component declaration securely from the nested Rapider API response.
 * @param {any} apiMessage - The raw message object from the backend API.
 * @returns {any | undefined} The extracted component object, or undefined.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function extractDisplayComponent(apiMessage: any): any | undefined {
  if (!apiMessage) return undefined;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const findRapiderData = (obj: any): any => {
    if (!obj) return null;
    let dataObj = obj.data;
    if (typeof dataObj === 'string') {
      try {
        dataObj = JSON.parse(dataObj);
      } catch (e) {
        /* silent fail */
      }
    }
    if (dataObj?.__rapider) return dataObj.__rapider;
    return null;
  };
  const rapiderObj =
    findRapiderData(apiMessage) ||
    findRapiderData(apiMessage.aiMessage) ||
    findRapiderData(apiMessage.message);
  return rapiderObj?.displayComponent;
}

/**
 * PURE FUNCTION: Extracts the transformation result (JSON context) from the backend response.
 * @param {any} apiMessage - The raw message object from the backend API.
 * @returns {any | undefined} The extracted transformation result, or undefined.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function extractTransformationResult(apiMessage: any): any | undefined {
  if (!apiMessage) return undefined;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const findRapiderData = (obj: any): any => {
    if (!obj) return null;
    let dataObj = obj.data;
    if (typeof dataObj === 'string') {
      try {
        dataObj = JSON.parse(dataObj);
      } catch (e) {
        /* silent fail */
      }
    }
    if (dataObj?.__rapider) return dataObj.__rapider;
    return null;
  };
  const rapiderObj =
    findRapiderData(apiMessage) ||
    findRapiderData(apiMessage.aiMessage) ||
    findRapiderData(apiMessage.message);
  return rapiderObj?.transformationResult || rapiderObj?.transformedData;
}

/**
 * PURE FUNCTION: Extracts a JSONata expression securely from the nested Rapider API response.
 * Safely navigates the payload to locate `data.__rapider.jsonataTransformation`.
 * @param {any} apiMessage - The raw message object from the backend API.
 * @returns {string | null} The extracted JSONata string, or null if validation fails.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function extractJsonataExpression(apiMessage: any): string | null {
  if (!apiMessage) return null;

  // Deep search utility to safely navigate stringified LoopBack JSON columns
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const findRapiderData = (obj: any): any => {
    if (!obj) return null;
    let dataObj = obj.data;
    if (typeof dataObj === 'string') {
      try {
        dataObj = JSON.parse(dataObj);
      } catch (e) {
        /* silent fail */
      }
    }
    if (dataObj?.__rapider) return dataObj.__rapider;
    return null;
  };

  const rapiderObj =
    findRapiderData(apiMessage) ||
    findRapiderData(apiMessage.aiMessage) ||
    findRapiderData(apiMessage.message);
  const expr = rapiderObj?.jsonataTransformation;

  if (typeof expr === 'string' && expr.trim() !== '') {
    // Trims and replaces escaped newlines safely before evaluation
    return expr.trim().replace(/\\n/g, '\n');
  }

  return null;
}

/**
 * PURE FUNCTION: Executes a JSONata expression against a given data context object.
 * @param {string} expression - The JSONata expression string.
 * @param {Record<string, any>} contextData - The data object to query and transform.
 * @returns {Promise<any>} The transformed data output.
 */
export async function executeJsonataTransformation(
  expression: string,
  contextData: Record<string, any>,
): Promise<any> {
  try {
    const compiledExpr = jsonata(expression);
    const result = await compiledExpr.evaluate(contextData);
    return result;
  } catch (error: any) {
    console.error('JSONata Evaluation Error:', error);
    throw new Error(
      `Failed to evaluate JSONata expression: ${error.message || 'Syntax error'}`,
    );
  }
}

/**
 * PURE FUNCTION: Replaces parts of the API path based on predefined redirection rules.
 * @param {string | undefined} path - The original API path.
 * @returns {string} The redirected API path.
 */
export function getRedirectedPath(path?: string): string {
  if (!path) return '';
  let newPath = path;
  const redirections = AI_CHAT_CONFIG.constants.apiPathRedirections || [];
  for (const rule of redirections) {
    if (newPath.includes(rule.pathPart)) {
      newPath = newPath.replace(rule.pathPart, rule.replaceWith);
    }
  }
  return newPath;
}

/**
 * PURE FUNCTION: Extracts the `actions` array securely from the nested Rapider API response.
 * Applies any global API path redirections automatically.
 * @param {any} apiMessage - The raw message object from the backend.
 * @returns {AiAction[] | undefined} The extracted array of actions or undefined.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function extractAiActions(apiMessage: any): AiAction[] | undefined {
  if (!apiMessage) return undefined;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const findRapiderData = (obj: any): any => {
    if (!obj) return null;
    let dataObj = obj.data;
    if (typeof dataObj === 'string') {
      try {
        dataObj = JSON.parse(dataObj);
      } catch (e) {
        /* silent fail */
      }
    }
    if (dataObj?.__rapider) return dataObj.__rapider;
    return null;
  };

  const rapiderObj =
    findRapiderData(apiMessage) ||
    findRapiderData(apiMessage.aiMessage) ||
    findRapiderData(apiMessage.message);

  if (rapiderObj?.actions && Array.isArray(rapiderObj.actions)) {
    return rapiderObj.actions.map((action: AiAction) => {
      if (action.payload?.http?.path) {
        return {
          ...action,
          payload: {
            ...action.payload,
            http: {
              ...action.payload.http,
              path: getRedirectedPath(action.payload.http.path),
            },
          },
        };
      }
      return action;
    });
  }
  return undefined;
}

/**
 * PURE FUNCTION: Extracts the `actionsUIConfig` mapping securely from the backend response.
 * @param {any} apiMessage - The raw message object from the backend.
 * @returns {AiActionsUIConfig | undefined} The extracted configuration or undefined.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function extractAiActionsUIConfig(
  apiMessage: any,
): AiActionsUIConfig | undefined {
  if (!apiMessage) return undefined;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const findRapiderData = (obj: any): any => {
    if (!obj) return null;
    let dataObj = obj.data;
    if (typeof dataObj === 'string') {
      try {
        dataObj = JSON.parse(dataObj);
      } catch (e) {
        /* silent fail */
      }
    }
    if (dataObj?.__rapider) return dataObj.__rapider;
    return null;
  };

  const rapiderObj =
    findRapiderData(apiMessage) ||
    findRapiderData(apiMessage.aiMessage) ||
    findRapiderData(apiMessage.message);

  if (rapiderObj?.actionsUIConfig) {
    return rapiderObj.actionsUIConfig;
  }
  return undefined;
}

/**
 * PURE FUNCTION: Converts a raw backend response into a playable audio Blob URL.
 * Specifies a fallback MIME type to ensure browser <audio> elements accept the media.
 * @param {any} rawData - The raw binary, text, or Blob response from the API.
 * @param {string} mimeType - The fallback MIME type (default 'audio/webm' as identified by hex dumps).
 * @returns {string} The created Object URL.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function createPlayableAudioUrl(
  rawData: any,
  mimeType: string = 'audio/webm',
): string {
  if (!rawData) throw new Error('No raw data provided for audio URL creation.');

  const blob =
    rawData instanceof Blob
      ? new Blob([rawData], { type: rawData.type || mimeType })
      : new Blob([rawData], { type: mimeType });

  return URL.createObjectURL(blob);
}

/**
 * PURE FUNCTION: Determines whether a thread's metadata should be refreshed.
 * Evaluates to true if the title is empty or matches the default placeholder title.
 * @param {string | undefined} currentTitle - The active thread's current title.
 * @param {string} defaultTitle - The system's default fallback title.
 * @returns {boolean} True if the title implies the thread should be refreshed.
 */
export function shouldFetchThreadMetadata(
  currentTitle: string | undefined,
  defaultTitle: string,
): boolean {
  if (!currentTitle) return true;
  return currentTitle.trim() === defaultTitle.trim();
}

/**
 * Formats a Date object into a localized, human-readable time string (e.g., "10:30 AM").
 * @param {Date | string} date - The date to format.
 * @returns {string} The formatted time string.
 */
export function formatMessageTime(date: Date | string): string {
  if (!date) return '';
  const d = typeof date === 'string' ? new Date(date) : date;
  return new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(d);
}

/**
 * Formats a Date object into a short string for the Thread sidebar (e.g., "Oct 24, 10:30 AM").
 * @param {Date | string} date - The date to format.
 * @returns {string} The formatted thread date string.
 */
export function formatThreadDate(date: Date | string): string {
  if (!date) return '';
  const d = typeof date === 'string' ? new Date(date) : date;
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(d);
}

/**
 * Formats a duration in seconds into a "MM:SS" string for the voice recording UI.
 * @param {number} totalSeconds - Total duration in seconds.
 * @returns {string} The formatted duration string.
 */
export function formatRecordingDuration(totalSeconds: number): string {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

/**
 * Calculates a human-readable file size string from raw bytes.
 * @param {number} bytes - File size in bytes.
 * @returns {string} Formatted file size (e.g., "2.5 MB").
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

/**
 * Determines the FontAwesome semantic icon class based on the file's MIME type or extension.
 * @param {string} fileName - The name of the file.
 * @param {string} mimeType - The MIME type of the file.
 * @returns {string} The semantic FontAwesome icon class string.
 */
export function getFileIconClass(fileName: string, mimeType: string): string {
  const ext = fileName.split('.').pop()?.toLowerCase() || '';
  const type = mimeType.toLowerCase();

  if (
    type.startsWith('image/') ||
    ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)
  )
    return 'fas fa-file-image text-info';
  if (type.startsWith('video/') || ['mp4', 'mov', 'avi'].includes(ext))
    return 'fas fa-file-video text-danger';
  if (type.startsWith('audio/') || ['mp3', 'wav', 'm4a'].includes(ext))
    return 'fas fa-file-audio text-warning';
  if (type === 'application/pdf' || ext === 'pdf')
    return 'fas fa-file-pdf text-danger';
  if (['doc', 'docx'].includes(ext)) return 'fas fa-file-word text-primary';
  if (['xls', 'xlsx', 'csv'].includes(ext))
    return 'fas fa-file-excel text-success';
  if (['zip', 'rar', '7z'].includes(ext))
    return 'fas fa-file-archive text-secondary-text';

  return 'fas fa-file-alt text-secondary-text';
}

/**
 * Validates an uploaded file against maximum size constraints.
 * @param {File} file - The physical file to validate.
 * @param {number} maxSizeMB - Maximum allowed size in megabytes.
 * @returns {{isValid: boolean, error?: string}} Validation result.
 */
export function validateFileAttachment(
  file: File,
  maxSizeMB: number = 10,
): { isValid: boolean; error?: string } {
  const maxBytes = maxSizeMB * 1024 * 1024;
  if (file.size > maxBytes) {
    return { isValid: false, error: AI_CHAT_CONFIG.texts.fileTooLarge };
  }
  return { isValid: true };
}

// --- Pure Functions for Dynamic Entity Rendering ---

/**
 * PURE FUNCTION: Extracts strictly typed entity data arrays from nested API responses.
 * Now aggressively checks stringified JSON structures often returned by LoopBack.
 * @param {any} apiMessage - The raw message object from the backend.
 * @returns {{entityName: string, data: any[]}|undefined} Clean extracted entity set or undefined.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function extractEntityData(
  apiMessage: any,
): { entityName: string; data: any[] } | undefined {
  if (!apiMessage) return undefined;

  // Deep search utility to safely navigate stringified LoopBack JSON columns
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const findEntityData = (obj: any): any => {
    console.log('🔍 Searching for entityData in object:', obj);
    if (!obj) return null;
    if (obj.entityData) return obj.entityData;
    if (obj.data) {
      let dataObj = obj.data;
      if (typeof dataObj === 'string') {
        try {
          dataObj = JSON.parse(dataObj);
        } catch (e) {
          /* silent fail */
        }
      }
      if (dataObj?.entityData) return dataObj.entityData;
      if (dataObj?.__rapider?.entityData) return dataObj.__rapider.entityData;
    }
    return null;
  };

  const payload =
    findEntityData(apiMessage) ||
    findEntityData(apiMessage.aiMessage) ||
    findEntityData(apiMessage.message);

  if (
    payload &&
    payload[0] &&
    typeof payload[0].entityName === 'string' &&
    Array.isArray(payload[0].data)
  ) {
    return {
      entityName: payload[0].entityName,
      data: payload[0].data,
    };
  }
  return undefined;
}

/**
 * PURE FUNCTION: Extracts the List Grid configurations safely from the Project Model metadata array.
 * @param {any} projectModel - Cached representation of the project model.
 * @returns {any|null} The resolved schema configuration, or null if unresolvable.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function extractListGridConfig(projectModel: any): any | null {
  if (!projectModel?.metadata || !Array.isArray(projectModel.metadata))
    return null;
  const uiConfigMeta = projectModel.metadata.find(
    (m: any) => m.key === 'uiEntityConfig',
  );
  if (!uiConfigMeta?.value) return null;
  try {
    const parsed =
      typeof uiConfigMeta.value === 'string'
        ? JSON.parse(uiConfigMeta.value)
        : uiConfigMeta.value;
    return parsed?.config?.listGridConfig || null;
  } catch (e) {
    return null;
  }
}

/**
 * PURE FUNCTION: Generates a scaffolded List Grid configuration strictly based on input payload types.
 * @param {any[]} data - Extracted array of data payload objects.
 * @returns {any} Structured grid object configuration dictating column formats.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function generateDefaultListGridConfig(data: any[]): any {
  if (!data || data.length === 0) return { columns: [] };
  const firstItem = data[0];
  const columns = Object.keys(firstItem).map((key) => ({
    fieldName: key,
    // Add spaces before capital letters and capitalize the first char
    title:
      key.charAt(0).toUpperCase() +
      key
        .slice(1)
        .replace(/([A-Z])/g, ' $1')
        .trim(),
    type: typeof firstItem[key] === 'number' ? 'number' : 'text',
    visible: true,
  }));
  return { columns, searchable: true, filterable: true, sortable: true };
}

/**
 * PURE FUNCTION: Generates the case-insensitive LoopBack search criteria needed to find Project Models dynamically.
 * @param {string} entityName - Target Project Model designation.
 * @returns {object} The filter configuration object.
 */
export function buildProjectModelFindFilter(entityName: string) {
  return {
    where: { name: entityName },
  };
}

// --- Pure Functions for NgRx & Backend Integration ---

/**
 * Generates a pseudo-random temporary ID for optimistic UI rendering before backend confirmation.
 * @returns {string} Temporary ID starting with "temp-".
 */
export function generateTempId(): string {
  return 'temp-' + Math.random().toString(36).substring(2, 11);
}

/**
 * Maps a raw API thread response object into the internal strictly typed ChatThread interface.
 * @param {any} apiThread - The raw thread data from the backend.
 * @returns {ChatThread} Normalized thread object.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function mapApiThreadToChatThread(apiThread: any): ChatThread {
  return {
    ...apiThread,
    id: apiThread.id,
    title: apiThread.title || AI_CHAT_CONFIG.constants.defaultThreadTitle,
    channelId: apiThread.channelId,
    createdDate: new Date(
      apiThread.createdDate || apiThread.createdAt || Date.now(),
    ),
    status: apiThread.status,
  };
}

/**
 * PURE FUNCTION: Safely extracts the message text or transcription from a raw API message.
 * Solves the missing transcript issue by falling back to the data layer when 'message' is empty.
 * @param {any} apiMessage - The raw message object from the backend.
 * @returns {string} The resolved text to display.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function extractApiMessageText(apiMessage: any): string {
  if (!apiMessage) return '';

  let dataObj = apiMessage.data;
  if (typeof dataObj === 'string') {
    try {
      dataObj = JSON.parse(dataObj);
    } catch (e) {
      /* ignore */
    }
  }

  // Priority 1: __rapider.text (structured AI response)
  if (dataObj?.__rapider?.text) return dataObj.__rapider.text;
  if (apiMessage.aiMessage?.data?.__rapider?.text)
    return apiMessage.aiMessage.data.__rapider.text;

  // Priority 2: Standard AI message body
  if (apiMessage.aiMessage?.message) return apiMessage.aiMessage.message;

  // Priority 3: Root text fallbacks
  if (dataObj?.text) return dataObj.text;
  if (dataObj?.message) return dataObj.message;

  return apiMessage.message || apiMessage.text || '';
}

/**
 * PURE FUNCTION: Extracts AI reasoning/thinking data from either `<think>` tags in the raw text
 * or structured payload properties. Supports parsing arrays into sequential ReasoningStep objects.
 * Prevents DOMPurify from stripping out the <think> blocks and destroying the reasoning content.
 * @param {any} apiMessage - The raw API message object.
 * @param {string} rawText - The unparsed message text.
 * @returns {{ reasoningText: string | undefined, reasoningSteps: ReasoningStep[] | undefined, cleanText: string }}
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function extractReasoningData(
  apiMessage: any,
  rawText: string,
): {
  reasoningText: string | undefined;
  reasoningSteps: ReasoningStep[] | undefined;
  cleanText: string;
} {
  let cleanText = rawText || '';
  let reasoningText: string | undefined = undefined;
  let reasoningSteps: ReasoningStep[] | undefined = undefined;

  // 1. Extract from <think> tags (Common in DeepSeek/Ollama models)
  const thinkRegex = /<think>([\s\S]*?)<\/think>/gi;
  const match = cleanText.match(thinkRegex);

  if (match && match.length > 0) {
    reasoningText = match
      .map((m) => m.replace(/<\/?think>/gi, '').trim())
      .join('\n\n');
    cleanText = cleanText.replace(thinkRegex, '').trim();
  }

  // 2. Extract from structured payload (Common in Rapider/OpenAI format)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const findData = (obj: any, key: string): any => {
    if (!obj) return null;
    let dataObj = obj.data;
    if (typeof dataObj === 'string') {
      try {
        dataObj = JSON.parse(dataObj);
      } catch (e) {
        /* silent fail */
      }
    }
    if (dataObj?.__rapider?.[key]) return dataObj.__rapider[key];
    if (dataObj?.[key]) return dataObj[key];
    return obj[key];
  };

  const payloadReasoningSteps =
    findData(apiMessage, 'reasoningSteps') ||
    findData(apiMessage.aiMessage, 'reasoningSteps');
  const payloadReasoningText =
    findData(apiMessage, 'reasoning') ||
    findData(apiMessage.aiMessage, 'reasoning');

  if (Array.isArray(payloadReasoningSteps)) {
    reasoningSteps = payloadReasoningSteps.map((step: any) => ({
      id: step.id || step.runId || undefined,
      title: step.title || step.name || step.step || undefined,
      content: step.content || step.description || step.message || String(step),
      status: step.status || 'completed',
    }));
  } else if (Array.isArray(payloadReasoningText)) {
    reasoningSteps = payloadReasoningText.map((step: any) => ({
      id: step.id || step.runId || undefined,
      title: step.title || step.name || undefined,
      content: step.content || step.description || String(step),
      status: step.status || 'completed',
    }));
  } else if (payloadReasoningText) {
    const parsedText =
      typeof payloadReasoningText === 'string'
        ? payloadReasoningText
        : JSON.stringify(payloadReasoningText, null, 2);
    reasoningText = reasoningText
      ? `${reasoningText}\n\n${parsedText}`
      : parsedText;
  }

  return { reasoningText, reasoningSteps, cleanText };
}

/**
 * PURE FUNCTION: Extracts InteractionConfig safely from the nested LoopBack payload.
 * Supports both root data and deeply nested '__rapider' blocks from the AI orchestrator.
 * @param {any} apiMessage - The raw message data from the backend.
 * @returns {InteractionConfig | undefined} The parsed interaction configuration.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function extractInteractionConfig(
  apiMessage: any,
): InteractionConfig | undefined {
  if (!apiMessage) return undefined;

  const findData = (obj: any, key: string): any => {
    if (!obj) return null;
    let dataObj = obj.data;
    if (typeof dataObj === 'string') {
      try {
        dataObj = JSON.parse(dataObj);
      } catch (e) {
        /* silent fail */
      }
    }
    if (dataObj?.__rapider?.[key]) return dataObj.__rapider[key];
    if (dataObj?.[key]) return dataObj[key];
    return obj[key];
  };

  const raw =
    findData(apiMessage, 'interactionConfig') ||
    findData(apiMessage.aiMessage, 'interactionConfig');
  if (!raw) return undefined;
  return typeof raw === 'string' ? JSON.parse(raw) : raw;
}

/**
 * PURE FUNCTION: Extracts Tool Calls safely from the nested LoopBack payload.
 * @param {any} apiMessage - The raw message data.
 * @returns {AiToolCall[] | undefined}
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function extractToolCalls(apiMessage: any): AiToolCall[] | undefined {
  if (!apiMessage) return undefined;
  const findData = (obj: any, key: string): any => {
    if (!obj) return null;
    let dataObj = obj.data;
    if (typeof dataObj === 'string') {
      try {
        dataObj = JSON.parse(dataObj);
      } catch (e) {
        /* silent fail */
      }
    }
    if (dataObj?.__rapider?.[key]) return dataObj.__rapider[key];
    if (dataObj?.[key]) return dataObj[key];
    return obj[key];
  };

  const raw =
    findData(apiMessage, 'toolCalls') ||
    findData(apiMessage.aiMessage, 'toolCalls');
  return Array.isArray(raw) ? raw : undefined;
}

/**
 * PURE FUNCTION: Extracts Async Progress tracking safely from the nested LoopBack payload.
 * @param {any} apiMessage - The raw message data.
 * @returns {AsyncProgress | undefined}
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function extractAsyncProgress(
  apiMessage: any,
): AsyncProgress | undefined {
  if (!apiMessage) return undefined;
  const findData = (obj: any, key: string): any => {
    if (!obj) return null;
    let dataObj = obj.data;
    if (typeof dataObj === 'string') {
      try {
        dataObj = JSON.parse(dataObj);
      } catch (e) {
        /* silent fail */
      }
    }
    if (dataObj?.__rapider?.[key]) return dataObj.__rapider[key];
    if (dataObj?.[key]) return dataObj[key];
    return obj[key];
  };

  const rawStr =
    findData(apiMessage, 'asyncProgress') ||
    findData(apiMessage.aiMessage, 'asyncProgress');
  if (rawStr) {
    const raw = typeof rawStr === 'string' ? JSON.parse(rawStr) : rawStr;
    if (raw.resultMarkdown && raw.steps && raw.steps.length > 0) {
      const lastStep = raw.steps[raw.steps.length - 1];
      if (!lastStep.resultMarkdown && !lastStep.result) {
        lastStep.resultMarkdown = raw.resultMarkdown;
      }
    }
    return raw;
  }

  // Look for asyncCompletion to mark a completed workflow
  const asyncCompletion =
    findData(apiMessage, 'asyncCompletion') ||
    findData(apiMessage.aiMessage, 'asyncCompletion');
  if (asyncCompletion) {
    let finalSteps = asyncCompletion.steps;
    const messageText =
      apiMessage.message ||
      apiMessage.data?.__rapider?.text ||
      apiMessage.text ||
      '';

    if (!finalSteps || finalSteps.length === 0) {
      const parsedSteps = messageText
        .split('\n')
        .filter((line: string) => line.trim().startsWith('-'))
        .map((line: string) => ({
          title: line.replace('-', '').trim(),
          status: 'completed' as const,
        }));
      finalSteps =
        parsedSteps.length > 0
          ? parsedSteps
          : [
              {
                title: 'Background Process Completed',
                status: 'completed' as const,
              },
            ];
    }

    // Attach response text to the last step so it can be viewed in the UI
    if (finalSteps.length > 0) {
      const lastStep = finalSteps[finalSteps.length - 1];
      if (!lastStep.resultMarkdown && !lastStep.result) {
        lastStep.resultMarkdown = asyncCompletion.resultMarkdown || messageText;
      }
    }

    const workflowTitle =
      apiMessage.relatedWorkflowTriggerEvent?.title ||
      apiMessage.workflow?.title ||
      apiMessage.title ||
      apiMessage.workflow?.name ||
      apiMessage.name ||
      'Workflow';

    return {
      percentage: 100,
      statusText: `${workflowTitle} Complete`,
      isPolling: false,
      correlationId: asyncCompletion.correlationId,
      steps: finalSteps,
      resultMarkdown: asyncCompletion.resultMarkdown || messageText,
    };
  }

  // Look for correlationId in metadata for an in-progress workflow
  const metadata =
    apiMessage.metadata ||
    apiMessage.aiMessage?.metadata ||
    apiMessage.message?.metadata;
  if (metadata && Array.isArray(metadata)) {
    const correlationMeta = metadata.find(
      (m: any) => m.key === 'correlationId' || m.key === 'correlationid',
    );
    if (correlationMeta) {
      return {
        percentage: 0,
        statusText: 'Executing Workflow...',
        isPolling: true,
        correlationId: correlationMeta.value,
        steps: [],
      };
    }
  }

  return undefined;
}

/**
 * PURE FUNCTION: Extracts the voice file ID from the backend message payload if available.
 * Required to refetch audio blobs if the local cache is destroyed by a page refresh.
 * @param {any} apiMessage - The raw message object from the backend.
 * @returns {string | undefined} The extracted projectFileId.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function extractVoiceFileId(apiMessage: any): string | undefined {
  let dataObj = apiMessage?.data;
  if (typeof dataObj === 'string') {
    try {
      dataObj = JSON.parse(dataObj);
    } catch (e) {
      /* ignore */
    }
  }

  if (dataObj?.fileAttachments && Array.isArray(dataObj.fileAttachments)) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const audioFile = dataObj.fileAttachments.find(
      (f: any) =>
        f.originalFilename?.endsWith('.wav') ||
        f.originalFilename?.endsWith('.mp3') ||
        f.originalFilename?.endsWith('.m4a') ||
        f.originalFilename?.endsWith('.webm'),
    );
    if (audioFile) return audioFile.id;
  }
  return undefined;
}

/**
 * Determines if a raw API message represents a voice recording.
 * Extracted so the mapping function cleanly resolves message intent.
 * @param {any} apiMessage - The raw message object from the backend.
 * @returns {boolean} True if it is a voice message.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function determineIfVoiceMessage(apiMessage: any): boolean {
  if (!apiMessage) return false;
  if (apiMessage.type === 'audio') return true;

  const isUser = apiMessage.senderId !== AI_CHAT_CONFIG.constants.aiReceiverId;
  // FIX: Only assume it's a voice message if it is from the user AND has a transcribed message payload
  if (
    isUser &&
    (apiMessage.data?.transcribedMessage || apiMessage.transcribedMessage)
  )
    return true;

  if (extractVoiceFileId(apiMessage)) return true;
  return false;
}

/**
 * PURE FUNCTION: Extracts file attachments securely from historical backend payloads.
 * Handles nested data structures typically returned by LoopBack.
 * @param {any} apiMessage - The raw message object from the backend.
 * @returns {FileAttachment[] | undefined} Array of parsed attachments or undefined.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function extractFileAttachments(
  apiMessage: any,
): FileAttachment[] | undefined {
  if (!apiMessage) return undefined;
  const attachments: FileAttachment[] = [];

  const safeAdd = (items: any[]) => {
    if (Array.isArray(items)) {
      items.forEach((item) => {
        if (
          item &&
          (item.url || item.id) &&
          !attachments.find((a) => a.id === item.id)
        ) {
          attachments.push({
            id: item.id || uuid(),
            name:
              item.fileName || item.originalFilename || item.name || 'document',
            size: item.size || 0,
            mimeType: item.mimeType || item.mimetype || 'unknown',
            url: item.url || undefined,
            extension: item.extension, // Map the explicit extension from the backend
          });
        }
      });
    }
  };

  // 1. Check standard message data payload
  if (apiMessage.data?.fileAttachments)
    safeAdd(apiMessage.data.fileAttachments);
  if (apiMessage.fileAttachments) safeAdd(apiMessage.fileAttachments);
  if (apiMessage.file) safeAdd(apiMessage.file);
  if (apiMessage.data?.attachments) safeAdd(apiMessage.data.attachments);

  // 2. Check inner __rapider property
  if (apiMessage.data?.__rapider?.attachments)
    safeAdd(apiMessage.data.__rapider.attachments);

  // 3. Check asyncCompletion for files generated during a background workflow
  if (apiMessage.data?.asyncCompletion?.attachments)
    safeAdd(apiMessage.data.asyncCompletion.attachments);
  if (apiMessage.data?.__rapider?.asyncCompletion?.attachments)
    safeAdd(apiMessage.data.__rapider.asyncCompletion.attachments);

  return attachments.length > 0 ? attachments : undefined;
}

/**
 * Maps a raw API message object into the internal strictly typed AIChatMessage interface.
 * Handles both standard text inputs and audio transcriptions automatically.
 * @param {any} apiMessage - The raw message data from the backend.
 * @returns {AIChatMessage} Normalized chat message object.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function mapApiMessageToAiChatMessage(apiMessage: any): AIChatMessage {
  const isUser = apiMessage.senderId !== AI_CHAT_CONFIG.constants.aiReceiverId;
  const rawText = extractApiMessageText(apiMessage);
  const { reasoningText, reasoningSteps, cleanText } = extractReasoningData(
    apiMessage,
    rawText,
  );
  const text = cleanText;
  const isVoiceMessage = determineIfVoiceMessage(apiMessage);
  const voiceFileId = extractVoiceFileId(apiMessage);
  const transcription = parseVoiceTranscription(apiMessage);
  const extractedAttachments = extractFileAttachments(apiMessage);
  let type = apiMessage.type || (isUser ? 'user-message' : 'ai-message');

  // Clean up NgRx action type bleed-overs natively
  if (type === '[AI Chat] Send Message') {
    type = isUser ? 'user-message' : 'ai-message';
  }

  // CRITICAL FIX: Intercept the confirmation flags explicitly
  const isConfirmation = apiMessage.data?.isConfirmation === true;
  if ((type === 'info' && isConfirmation) || isConfirmation) {
    type = 'action-response';
  }

  // STRICT ENFORCEMENT: Normalize all legacy and dynamic types into the core types
  const allowedTypes = [
    'user-message',
    'ai-message',
    'action-response',
    'workflow-step',
  ];
  if (type === 'workflow-status-update') {
    type = 'action-response';
  } else if (!allowedTypes.includes(type)) {
    type = isUser ? 'user-message' : 'ai-message';
  }

  // CRITICAL ENFORCEMENT: If the sender is the AI Assistant (0000...1), it MUST be an ai-message
  // This prevents the backend from mislabeling AI responses as user-messages or workflow-steps
  if (!isUser && type !== 'action-response') {
    type = 'ai-message';
  }

  // ENFORCEMENT: If type is explicitly 'ai-message', it cannot be a user message.
  let finalIsUser = isUser;
  if (type === 'ai-message') {
    finalIsUser = false;
  }

  const jsonataTransformation = extractJsonataExpression(apiMessage);
  const displayTemplate = extractDisplayTemplate(apiMessage);
  const template = extractTemplate(apiMessage);
  const displayComponent = extractDisplayComponent(apiMessage);
  const transformationResult = extractTransformationResult(apiMessage);
  const actions = extractAiActions(apiMessage);
  const workflowPlanUpdate = extractWorkflowPlanUpdate(apiMessage);

  // Extract Interaction Config securely
  const interactionConfig = extractInteractionConfig(apiMessage);

  const toolCalls = extractToolCalls(apiMessage);
  const asyncProgress = extractAsyncProgress(apiMessage);

  // Extract user feedback state natively
  const feedback =
    apiMessage.feedback || apiMessage.data?.feedback || undefined;

  if (workflowPlanUpdate) {
    type = 'action-response';
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const parsedData: any = parseApiMessageData(apiMessage);
  const actionResponseData = extractActionResponseData(apiMessage);

  if (
    actionResponseData?.originatedActionMessageId ||
    parsedData?.actionResult ||
    parsedData?.actionResponseStatus
  ) {
    type = 'action-response';
  }

  // Safely extract the audio URL if provided directly by the backend files array.
  // Will be undefined on refresh, prompting the component to fetch via voiceFileId.
  const voiceUrl =
    apiMessage.file && apiMessage.file.length > 0 && apiMessage.file[0].url
      ? apiMessage.file[0].url
      : undefined;

  // Extract the raw __rapider payload for developer debugging if available
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const findRapiderData = (obj: any): any => {
    if (!obj) return null;
    let dataObj = obj.data;
    if (typeof dataObj === 'string') {
      try {
        dataObj = JSON.parse(dataObj);
      } catch (e) {
        /* silent fail */
      }
    }
    if (dataObj?.__rapider) return dataObj.__rapider;
    return null;
  };
  const rawRapiderPayload =
    findRapiderData(apiMessage) ||
    findRapiderData(apiMessage.aiMessage) ||
    findRapiderData(apiMessage.message) ||
    apiMessage.data ||
    apiMessage.message?.data;

  // Intercept and normalize message visibility flags (hidden/system execution messages)
  const isHiddenExplicit =
    apiMessage.isHidden === true ||
    apiMessage.data?.isHidden === true ||
    apiMessage.metadata?.isHidden === true;
  const isShownInChatExplicit =
    apiMessage.isShownInChat === false ||
    apiMessage.data?.isShownInChat === false ||
    apiMessage.metadata?.isShownInChat === false;
  // Automatically identify execution result summaries (e.g. "Execute GoogleDriveCorporate...") that carry actionResult
  const isExecutionSummaryText =
    typeof rawText === 'string' &&
    (rawText.startsWith('Execute ') ||
      rawText.startsWith('Actions executed: ') ||
      rawText === 'Action executed' ||
      rawText === 'Action confirmed' ||
      rawText === 'Action cancelled.');
  const isImplicitlyHidden =
    isExecutionSummaryText &&
    (apiMessage.data?.actionResult || apiMessage.data?.actionResponseStatus);

  const isShownInChat =
    !isHiddenExplicit && !isShownInChatExplicit && !isImplicitlyHidden;
  const isHidden = !isShownInChat;

  return {
    id: apiMessage.id,
    text: text,
    isUser: finalIsUser,
    timestamp: new Date(
      apiMessage.createdDate || apiMessage.createdAt || Date.now(),
    ),
    type: type,
    isConfirmation: isConfirmation,
    status: apiMessage.status,
    aiFunctionId: apiMessage.aiFunctionId,
    isVoiceMessage: isVoiceMessage,
    voiceUrl: voiceUrl,
    voiceFileId: voiceFileId,
    transcribedMessage: transcription || undefined,
    originatingRequestMessageId: apiMessage.userMessage?.id || undefined,
    actionResponseData: actionResponseData,
    data: parsedData,
    entityData: extractEntityData(apiMessage), // Resolves via deep search utility
    actions: actions,
    actionsUIConfig: extractAiActionsUIConfig(apiMessage),
    workflowPlan: extractWorkflowPlan(apiMessage),
    workflowPlanUpdate: workflowPlanUpdate,
    workflowStepData: extractWorkflowStepData(apiMessage),
    jsonataTransformation: jsonataTransformation || undefined,
    displayTemplate: displayTemplate || undefined,
    template: template || undefined,
    displayComponent: displayComponent || undefined,
    transformationResult: transformationResult || undefined,
    reasoningText: reasoningText,
    reasoningSteps: reasoningSteps,
    feedback: feedback,
    interactionConfig: interactionConfig,
    interactionState: undefined, // Defaults to pending natively, preventing interaction locking on fresh loads
    toolCalls: toolCalls,
    rawRapiderPayload: rawRapiderPayload,
    memoryUpdates: extractMemoryUpdates(rawRapiderPayload),
    asyncProgress: asyncProgress,
    attachments: extractedAttachments || apiMessage.attachments,
    isShownInChat: isShownInChat,
    isHidden: isHidden,
  };
}

/**
 * PURE FUNCTION: reads the Company Knowledge notes saved during the turn from the `__rapider`
 * payload, keeping only well-formed entries.
 *
 * @param rapiderPayload The message's raw `__rapider` payload (any shape).
 * @returns The saved notes, or undefined when the turn saved none.
 */
export function extractMemoryUpdates(rapiderPayload: unknown): MemoryUpdate[] | undefined {
  const updates = (rapiderPayload as { memoryUpdates?: unknown } | null | undefined)?.memoryUpdates;
  if (!Array.isArray(updates)) {
    return undefined;
  }
  const valid = updates.filter(
    (u): u is MemoryUpdate => !!u && typeof u === 'object' && typeof (u as MemoryUpdate).pageKey === 'string' && typeof (u as MemoryUpdate).title === 'string',
  );
  return valid.length > 0 ? valid : undefined;
}

/**
 * PURE FUNCTION: query parameters that open a saved note in the Knowledge notes view (with the
 * private flag for notes saved to the person's own space).
 *
 * @param note The saved note.
 * @returns Query parameters for `AI_CHAT_CONFIG.constants.knowledgeNotesRoute`.
 */
export function buildMemoryNoteQueryParams(note: MemoryUpdate): Record<string, string> {
  const constants = AI_CHAT_CONFIG.constants;
  const queryParams: Record<string, string> = { [constants.knowledgeNoteQueryParam]: note.pageKey };
  if (note.scope === constants.knowledgePrivateScope) {
    queryParams[constants.knowledgePrivateQueryParam] = constants.knowledgePrivateQueryValue;
  }
  return queryParams;
}

/**
 * PURE FUNCTION: label of a saved-note chip (company vs private knowledge).
 *
 * @param note The saved note.
 * @returns The localized chip label.
 */
export function memoryNoteLabel(note: MemoryUpdate): string {
  return note.scope === AI_CHAT_CONFIG.constants.knowledgePrivateScope
    ? AI_CHAT_CONFIG.texts.memorySavedPrivateLabel
    : AI_CHAT_CONFIG.texts.memorySavedLabel;
}

/**
 * Reads the saved Company Knowledge notes (`memoryUpdates`) from a send/stream response, looking in
 * the same places the other `extract*` helpers look (`data.__rapider`, `aiMessage`, `message`).
 *
 * @param apiMessage The raw response of the send/stream call.
 * @returns The saved notes, or undefined when the turn saved none.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function extractMemoryUpdatesFromResponse(apiMessage: any): MemoryUpdate[] | undefined {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const findRapider = (obj: any): unknown => {
    if (!obj) return null;
    let dataObj = obj.data;
    if (typeof dataObj === 'string') {
      try { dataObj = JSON.parse(dataObj); } catch (e) { /* silent fail */ }
    }
    return dataObj?.__rapider ?? obj.__rapider ?? null;
  };
  for (const candidate of [apiMessage, apiMessage?.aiMessage, apiMessage?.message, apiMessage?.publicResponse]) {
    const updates = extractMemoryUpdates(findRapider(candidate));
    if (updates) return updates;
  }
  return undefined;
}

/**
 * Builds the LoopBack JSON filter for finding a Direct Message Channel between the user and AI.
 * Used in Step 1 of the thread initialization flow.
 * @param {string} senderId - Current logged-in User ID
 * @param {string} receiverId - Target AI Assistant User ID
 * @returns {string} Stringified LoopBack filter.
 */
export function buildFindDirectMessageChannelFilter(
  senderId: string,
  receiverId: string,
): string {
  return JSON.stringify({
    where: {
      type: 'direct-message',
      memberIds: receiverId,
    },
  });
}

/**
 * Builds the LoopBack JSON filter for paginating through Threads inside a specific Channel.
 * Used in Step 4 of the thread initialization flow.
 * @param {string} channelId - The active Direct Message Channel ID.
 * @param {number} limit - Maximum number of threads to fetch.
 * @param {number} skip - Offset for pagination.
 * @returns {string} Stringified LoopBack filter.
 */
export function buildFindThreadsFilter(
  channelId: string,
  limit: number,
  skip: number,
): string {
  return JSON.stringify({
    where: { channelId: channelId },
    fields: AI_CHAT_CONFIG.constants.threadListFields,
    order: ['createdDate DESC'], // Fetch most recent threads first
    limit: limit,
    skip: skip,
  });
}

/**
 * Builds the LoopBack JSON filter for retrieving chronologically ordered messages in a specific thread.
 * @param {string} threadId - The active thread ID.
 * @returns {string} Stringified LoopBack filter.
 */
export function buildFindThreadMessagesFilter(threadId: string): string {
  return JSON.stringify({
    where: { threadId: threadId },
    order: ['createdDate ASC'],
  });
}

/**
 * Builds the structured payload expected by the SDK for creating a new thread.
 * If channelId is an empty string, the backend automatically generates the DM channel.
 * @param {string} title - Thread title.
 * @param {string} userQuery - The initial query that started the thread.
 * @param {string} channelId - Existing channel ID, or empty string to auto-generate.
 * @returns {any} The request payload wrapped in { body: {...} }.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function buildCreateThreadPayload(
  title: string,
  userQuery: string,
  channelId: string,
): any {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const payload: any = {
    title: title || AI_CHAT_CONFIG.constants.defaultThreadTitle,
    channelId: channelId, // Can be exactly '' to force backend generation
  };

  if (userQuery) {
    payload.userQuery = userQuery;
  }

  return { body: payload };
}

/**
 * Builds the structured payload expected by the SDK for sending a standard direct message.
 * @param {string} text - User's chat message.
 * @param {string} threadId - Target thread ID.
 * @param {string} receiverId - AI Assistant ID.
 * @param {string} aiFunctionId - Target AI function identifier for routing.
 * @returns {any} The request payload wrapped in { body: {...} }.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function buildDirectMessagePayload(
  text: string,
  threadId: string,
  receiverId: string,
  aiFunctionId: string = 'router-agent',
): any {
  return {
    body: {
      message: text,
      receiverId: receiverId,
      type: 'text',
      status: 'sent',
      threadId: threadId,
      aiFunctionId: aiFunctionId,
    },
  };
}

/**
 * Builds the structured payload expected by the SDK's `$FormData` method for messages with files.
 * @param {string} text - User's chat message.
 * @param {string} threadId - Target thread ID.
 * @param {string} receiverId - AI Assistant ID.
 * @param {FileAttachment[]} attachments - Standard uploaded files.
 * @param {File[]} explicitFiles - Explicit blob files (like voice recordings).
 * @param {string} aiFunctionId - Target AI function identifier for routing.
 * @returns {any} The request payload wrapped in { body: {...} }.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function buildDirectMessageFormDataPayload(
  text: string,
  threadId: string,
  receiverId: string,
  attachments: FileAttachment[] = [],
  explicitFiles: File[] = [],
  aiFunctionId = 'router-agent',
  type = 'text',
): any {
  const attachmentFiles = attachments
    .map((a) => a.file)
    .filter((f) => f !== undefined && f !== null) as File[];
  const allFiles = [...attachmentFiles, ...explicitFiles];

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const body: any = {
    message: text,
    receiverId: receiverId,
    type: type,
    status: 'sent',
    threadId: threadId,
    aiFunctionId: aiFunctionId, // THIS IS IMPORTANT: Do not remove
    responseMode: 'activity-stream', // Force streaming mode natively for reasoning steps
  };

  if (allFiles.length > 0) {
    body.file = allFiles;
  }

  return { body };
}

/**
 * PURE FUNCTION: Builds the LoopBack JSON filter for querying AI Functions that are exposed as features to the UI.
 * @returns {string} Stringified LoopBack filter.
 */
export function buildExposedAiFunctionsFilter(): string {
  return JSON.stringify({
    where: { exposeAsFeature: true },
    fields: ['id', 'name', 'title'],
  });
}

/**
 * Safely parses the complex backend response to extract the AI's textual response.
 * @param {any} response - The raw API response body.
 * @returns {string} Clean AI text string.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function parseAiResponseText(response: any): string {
  if (!response) return '';
  if (response.aiMessage?.message) return response.aiMessage.message;
  if (response.aiMessage?.data) {
    if (response.aiMessage.data.__rapider?.text)
      return response.aiMessage.data.__rapider.text;
    if (response.aiMessage.data.message) return response.aiMessage.data.message;
    if (response.aiMessage.data.text) return response.aiMessage.data.text;
  }
  if (response.__rapider?.text) return response.__rapider.text;
  if (response.text) return response.text;
  return '';
}

/**
 * Safely extracts a transcribed voice message from the backend payload.
 * @param {any} response - The raw API response body.
 * @returns {string | null} Transcription text, or null if none exists.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function parseVoiceTranscription(response: any): string | null {
  if (!response) return null;
  if (response.transcribedMessage) return response.transcribedMessage;
  if (response.data?.transcribedMessage)
    return response.data.transcribedMessage;
  const userMsgData = response.message || response.userMessage;
  if (typeof userMsgData === 'object' && userMsgData !== null) {
    if (userMsgData?.data?.transcribedMessage)
      return userMsgData.data.transcribedMessage;
    if (userMsgData?.transcribedMessage) return userMsgData.transcribedMessage;
  }
  if (response.aiMessage?.data?.transcribedMessage)
    return response.aiMessage.data.transcribedMessage;
  if (response.aiMessage?.transcribedMessage)
    return response.aiMessage.transcribedMessage;
  return null;
}

/**
 * PURE FUNCTION: Determines whether an AI action is a server-executed action (such as MCP tool actions).
 * Server actions run backend-side (e.g. via Nango) and do not have an HTTP payload for client execution.
 * @param {AiAction | undefined} action - The AI action to evaluate.
 * @returns {boolean} True if the action is configured for server execution.
 */
export function isServerAction(action: AiAction | undefined): boolean {
  if (!action) return false;
  return action.execution === 'server' || action.type === 'server_action';
}

/**
 * PURE FUNCTION: Checks if the provided array of AI Actions contains at least one 'endpoint' or 'server_action' type action.
 * Used to determine if the chat UI should wrap the data into an Entity Save Card.
 * @param {AiAction[]} actions - The array of actions to evaluate.
 * @returns {boolean} True if an endpoint or server action exists.
 */
export function hasEndpointAction(actions?: AiAction[]): boolean {
  if (!actions || !Array.isArray(actions)) return false;
  return actions.some((a) => a.type === 'endpoint' || isServerAction(a));
}

/**
 * PURE FUNCTION: Immutably adds a string value to a Javascript Set.
 * Ensures Angular signals trigger change detection correctly by creating a new reference.
 * @param {Set<string>} currentSet - The original set.
 * @param {string} valueToAdd - The value to insert.
 * @returns {Set<string>} A new Set containing the original elements plus the new value.
 */
export function pureAddStringToSet(
  currentSet: Set<string>,
  valueToAdd: string,
): Set<string> {
  const newSet = new Set(currentSet);
  newSet.add(valueToAdd);
  return newSet;
}

/**
 * PURE FUNCTION: Determines the Tailwind background color class for a given HTTP method.
 * @param {string} method - The HTTP method (e.g., 'POST', 'GET', 'PUT', 'DELETE').
 * @returns {string} The Tailwind background color class.
 */
export function getHttpMethodColorClass(method: string | undefined): string {
  const m = (method || '').toUpperCase();
  switch (m) {
    case 'GET':
      return 'bg-info text-white'; // Blue
    case 'POST':
      return 'bg-success text-white'; // Green
    case 'PUT':
    case 'PATCH':
      return 'bg-warning text-white'; // Yellow
    case 'DELETE':
      return 'bg-danger text-white'; // Red
    default:
      return 'bg-secondary-text text-white'; // Gray fallback
  }
}

/**
 * PURE FUNCTION: Immutably removes a string value from a Javascript Set.
 * Ensures Angular signals trigger change detection correctly when restoring a discarded action.
 * @param {Set<string>} currentSet - The original set.
 * @param {string} valueToRemove - The value to remove.
 * @returns {Set<string>} A new Set containing the original elements minus the removed value.
 */
export function pureRemoveStringFromSet(
  currentSet: Set<string>,
  valueToRemove: string,
): Set<string> {
  const newSet = new Set(currentSet);
  newSet.delete(valueToRemove);
  return newSet;
}

/**
 * PURE FUNCTION: Extracts and separates any field starting with `__rapider` from the entity data.
 * Prevents Angular lifecycle errors by guaranteeing a fresh object reference without mutating the original state.
 * @param {any} data - The raw entity data (object or array).
 * @returns {{ cleansed: any, extracted: any }} The separated clean payload and the metadata.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function separateRapiderMetadata(data: any): {
  cleansed: any;
  extracted: any;
} {
  const rapiderFieldKey = '__rapider';
  if (!data) return { cleansed: null, extracted: null };

  if (Array.isArray(data)) {
    const cleansedArr: any[] = [];
    const extractedArr: any[] = [];
    for (const item of data) {
      const cleansedItem: any = { ...item };
      const extracted: any = {};
      for (const key of Object.keys(item)) {
        if (key.startsWith(rapiderFieldKey)) {
          extracted[key] = cleansedItem[key];
          delete cleansedItem[key];
        }
      }
      cleansedArr.push(cleansedItem);
      extractedArr.push(Object.keys(extracted).length ? extracted : null);
    }
    return { cleansed: cleansedArr, extracted: extractedArr };
  }

  if (
    typeof data === 'object' &&
    data !== null &&
    Object.prototype.toString.call(data) === '[object Object]'
  ) {
    const cleansedObj: any = {};
    const extracted: any = {};
    for (const key of Object.keys(data)) {
      if (key.startsWith(rapiderFieldKey)) {
        extracted[key] = data[key];
      } else {
        cleansedObj[key] = data[key];
      }
    }
    return {
      cleansed: cleansedObj,
      extracted: Object.keys(extracted).length ? extracted : null,
    };
  }

  return { cleansed: data, extracted: null };
}

/**
 * PURE FUNCTION: Retrieves the title metadata for a specific index from the extracted metadata set.
 * @param {any} meta - The extracted metadata array or object.
 * @param {number} index - The current index.
 * @returns {string | null} The calculated title or null.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function getExtractedMetaTitlePure(
  meta: any,
  index: number,
): string | null {
  if (Array.isArray(meta)) {
    return meta[index] && meta[index]['__rapiderCalculatedTitle']
      ? meta[index]['__rapiderCalculatedTitle']
      : null;
  } else if (meta && typeof meta === 'object') {
    return meta['__rapiderCalculatedTitle'] || null;
  }
  return null;
}

/**
 * PURE FUNCTION: Retrieves the cleansed JSON payload for a specific index.
 * @param {any} data - The cleansed data array or object.
 * @param {number} index - The current index.
 * @returns {any} The targeted data object.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function getCleansedEntityDataAtPure(data: any, index: number): any {
  if (Array.isArray(data)) {
    return data[index];
  } else if (data && typeof data === 'object') {
    return data;
  }
  return null;
}

/**
 * PURE FUNCTION: Normalizes a data payload into an array format.
 * Useful for simplifying Angular template iterations over mixed object/array payloads.
 * @param {any} data - The raw data object or array.
 * @returns {any[]} An array containing the data.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function normalizePayloadToArray(data: any): any[] {
  if (data === null || data === undefined) return [];
  return Array.isArray(data) ? data : [data];
}

/**
 * PURE FUNCTION: Resolves the appropriate button labels for an action based on UI config or fallbacks.
 * Forces "Cancel" labels to visibly render as "Discard" to clarify the action for standard endpoint actions,
 * but retains "Confirm" / "Cancel" for server actions (MCP tool confirmation).
 * @param {AiAction} action - The target action definition.
 * @param {AiActionsUIConfig} uiConfig - The mapped UI configurations.
 * @returns {{ primary: string, secondary: string }} The resolved button texts.
 */
export function getActionButtonLabels(
  action: AiAction,
  uiConfig?: AiActionsUIConfig,
): { primary: string; secondary: string } {
  if (isServerAction(action)) {
    return {
      primary: AI_CHAT_CONFIG.texts.confirmActionPrimary,
      secondary: AI_CHAT_CONFIG.texts.cancelActionSecondary,
    };
  }

  const defaultLabels = {
    primary: AI_CHAT_CONFIG.texts.defaultActionPrimary,
    secondary: AI_CHAT_CONFIG.texts.defaultActionSecondary,
  };

  let primary = defaultLabels.primary;
  let secondary = defaultLabels.secondary;

  if (
    uiConfig?.operationButtons &&
    uiConfig.operationButtons[action.operation]
  ) {
    const configuredLabels = uiConfig.operationButtons[action.operation];
    primary = configuredLabels.primaryText || primary;
    secondary = configuredLabels.secondaryText || secondary;
  }

  // Force 'Cancel' to 'Discard' purely in the UI display
  if (secondary.toLowerCase() === 'cancel') {
    secondary = AI_CHAT_CONFIG.texts.defaultActionSecondary;
  }

  return { primary, secondary };
}

/**
 * PURE FUNCTION: Determines the icon and color class based on the operation type.
 * Intercepts discarded states to return a gray trash icon.
 * @param {string} operation - The operation type (e.g., 'create', 'update', 'delete', 'read', 'get', 'execute_capability').
 * @param {boolean} isDiscarded - Whether the action has been discarded by the user.
 * @param {AiAction} [action] - The full action object to check for server execution mode.
 * @returns {{ icon: string, colorClass: string, needsWhiteBackdrop: boolean }} Object containing styling instructions.
 */
export function getOperationIconAndColor(
  operation: string | undefined,
  isDiscarded: boolean = false,
  action?: AiAction,
): { icon: string; colorClass: string; needsWhiteBackdrop: boolean } {
  if (isDiscarded) {
    return {
      icon: 'fa-solid fa-trash',
      colorClass: 'text-secondary-text',
      needsWhiteBackdrop: false,
    };
  }

  if (action && isServerAction(action)) {
    return {
      icon: 'fa-solid fa-server fa-xl',
      colorClass: 'text-primary',
      needsWhiteBackdrop: false,
    };
  }

  // Strip the _onRapider suffix if present so standard operations still map to the correct semantic icons
  const op = (operation || '').toLowerCase().replace('_onrapider', '');
  if (op.includes('execute_capability') || op.startsWith('mcp:')) {
    return {
      icon: 'fa-solid fa-server fa-xl',
      colorClass: 'text-primary',
      needsWhiteBackdrop: false,
    };
  }
  switch (op) {
    case 'create':
      return {
        icon: 'fa-solid fa-square-plus fa-xl',
        colorClass: 'text-success',
        needsWhiteBackdrop: true,
      };
    case 'update':
      return {
        icon: 'fa-solid fa-pen-to-square fa-xl',
        colorClass: 'text-info',
        needsWhiteBackdrop: false,
      };
    case 'delete':
      return {
        icon: 'fa-solid fa-trash fa-xl',
        colorClass: 'text-danger',
        needsWhiteBackdrop: false,
      };
    case 'read':
    case 'get':
      return {
        icon: 'fa-solid fa-magnifying-glass fa-xl',
        colorClass: 'text-secondary-text',
        needsWhiteBackdrop: false,
      };
    default:
      return {
        icon: 'fa-solid fa-layer-plus fa-xl',
        colorClass: 'text-secondary-text',
        needsWhiteBackdrop: true,
      };
  }
}

/**
 * PURE FUNCTION: Filters a list of actions to return only those that have not been discarded.
 * @param {AiAction[] | undefined} actions - The array of actions.
 * @param {Set<string>} discardedSet - The set of descriptions representing discarded actions.
 * @returns {AiAction[]} Array of active actions.
 */
export function getActiveActions(
  actions: AiAction[] | undefined,
  discardedIndices: Set<number>,
): AiAction[] {
  if (!actions || !Array.isArray(actions)) return [];
  return actions.filter((a, i) => !discardedIndices.has(i));
}

/**
 * PURE FUNCTION: Builds a structured HTML/string representation of active actions for tooltips.
 * @param {AiAction[]} activeActions - Array of non-discarded actions.
 * @returns {string} Formatted string.
 */
export function buildExecuteAllTooltip(activeActions: AiAction[]): string {
  if (activeActions.length === 0) return 'No actions to execute.';
  return activeActions.map((a, i) => `${i + 1}. ${a.description}`).join('\n');
}

/**
 * PURE FUNCTION: Determines if an action can be safely re-executed manually by the user.
 * Safe operations are typically idempotent read operations like GET.
 * @param {AiAction} action - The action to evaluate.
 * @returns {boolean} True if the action is safely re-executable.
 */
export function isActionReExecutable(action: AiAction): boolean {
  if (!action || !action.operation) return false;
  const op = action.operation.toLowerCase();
  return op === 'get' || op === 'get_onrapider' || op === 'read';
}

/**
 * PURE FUNCTION: Determines semantic icon, title, and color for a given orchestrator workflow task identifier (name or type).
 * Maps specific AI Function names to unique visual representations within the timeline UI.
 * @param {string} taskIdentifier - The name or type of the task (e.g., 'RapiderProjectModelDataQuery' or 'project-model-data-query').
 * @returns {{ icon: string, colorClass: string, title: string }} Mapping definitions for UI templates.
 */
export function getWorkflowTaskTheme(taskIdentifier: string): {
  icon: string;
  colorClass: string;
  title: string;
} {
  switch (taskIdentifier) {
    case 'RapiderProjectModelDataQuery':
    case 'project-model-data-query':
      return {
        icon: 'fa-database',
        colorClass: 'text-info',
        title: AI_CHAT_CONFIG.texts.workflowTaskQuery,
      };
    case 'RapiderDataTransformationWithJsonata':
    case 'data-transformation-with-jsonata':
      return {
        icon: 'fa-code-branch',
        colorClass: 'text-warning',
        title: AI_CHAT_CONFIG.texts.workflowTaskTransform,
      };
    case 'RapiderDisplayTheResults':
    case 'display-the-results':
      return {
        icon: 'fa-chart-pie',
        colorClass: 'text-success',
        title: AI_CHAT_CONFIG.texts.workflowTaskDisplay,
      };
    case 'RapiderGeneralQuestion':
    case 'general-question':
      return {
        icon: 'fa-question-circle',
        colorClass: 'text-primary',
        title: AI_CHAT_CONFIG.texts.workflowTaskQuestion,
      };
    case 'RapiderUserSelectEntityRecord':
    case 'user-select-entity-record':
      return {
        icon: 'fa-mouse-pointer',
        colorClass: 'text-secondary-text',
        title: AI_CHAT_CONFIG.texts.workflowTaskSelect,
      };
    default:
      return {
        icon: 'fa-tasks',
        colorClass: 'text-secondary-text',
        title: AI_CHAT_CONFIG.texts.workflowTaskGeneric,
      };
  }
}

/**
 * PURE FUNCTION: Immutably adds multiple string values to a Javascript Set.
 * @param {Set<string>} currentSet - The original active set.
 * @param {string[]} valuesToAdd - Array of strings to uniformly append.
 * @returns {Set<string>} A new populated Set.
 */
export function pureAddMultipleStringsToSet(
  currentSet: Set<string>,
  valuesToAdd: string[],
): Set<string> {
  const newSet = new Set(currentSet);
  valuesToAdd.forEach((val) => newSet.add(val));
  return newSet;
}

/**
 * PURE FUNCTION: Creates a new chat message representing the individual result of an endpoint action execution.
 * Automatically maps the response payload into a renderable grid entity if a returnType is defined.
 * @param {AiAction} action - The action that was executed.
 * @param {boolean} isSuccess - Whether the execution was successful.
 * @param {any} responseData - The raw response or error data.
 * @returns {AIChatMessage} A newly constructed chat message object.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function createEndpointResponseMessage(
  action: AiAction,
  isSuccess: boolean,
  responseData: any,
): AIChatMessage {
  const httpConfig = action.payload.http;
  const responseMessages = httpConfig?.responseMessages;

  let messageText = '';
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let parsedData: any = null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let entityData: { entityName: string; data: any[] } | undefined = undefined;

  if (isSuccess) {
    messageText = responseMessages?.success || 'Request completed successfully';

    if (typeof responseData === 'string' && responseData.trim() !== '') {
      try {
        parsedData = JSON.parse(responseData);
      } catch (e) {
        parsedData = { response: responseData };
      }
    } else {
      parsedData = responseData;
    }

    // Map to grouped entity for RapiderListGrid rendering if a returnType is specified
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const returnType =
      httpConfig?.returnType || (httpConfig as any)?.projectModelName;

    if (returnType && parsedData) {
      const dataArray = Array.isArray(parsedData) ? parsedData : [parsedData];
      entityData = {
        entityName: returnType,
        data: dataArray,
      };
    }
  } else {
    messageText = responseMessages?.error || `Request failed: ${responseData}`;
  }

  return {
    id: uuid(),
    text: messageText,
    isUser: false, // Represented as a System/AI response in the chat thread
    timestamp: new Date(),
    entityData: entityData,
    isError: !isSuccess,
  };
}

/**
 * PURE FUNCTION: Builds the backend payload to save the execution result of an action into the chat context.
 * Maps the response payload directly into the required data schema as an invisible system context message.
 * For server actions (MCP tools), outputs { status: 'success', confirmed: boolean } in data.actionResult[] per the frontend MCP confirmation contract.
 * @param {AiAction} action - The action that was executed.
 * @param {string | null} threadId - The active thread ID.
 * @param {boolean} isSuccess - Whether the execution was successful (or confirmed for server actions).
 * @param {any} responseOrError - The raw response data or error message.
 * @param {number} status - The HTTP status code.
 * @param {string | undefined} originatingRequestMessageId - The ID of the original message that requested this action.
 * @param {string} originatedActionMessageId - The ID of the message containing the actual action that was executed.
 * @param {number} actionIndex - The index of the action in the original message's action array.
 * @returns {any} The formatted payload for the backend message controller.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function buildActionExecutionBackendPayload(
  action: AiAction,
  threadId: string | null,
  isSuccess: boolean,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  responseOrError: any,
  status = 200,
  originatingRequestMessageId: string | undefined,
  originatedActionMessageId: string,
  actionIndex: number,
): any {
  const actionResultArray = new Array(actionIndex + 1).fill(null);

  if (isServerAction(action)) {
    // Per Frontend MCP Confirmation Spec §3:
    // For server actions, report decision as { status: 'success', confirmed: true/false } at top level
    const messageText = isSuccess ? 'Action confirmed.' : 'Action cancelled.';
    actionResultArray[actionIndex] = {
      status: 'success', // ALWAYS 'success' per spec §3.3 even when cancelled
      confirmed: isSuccess,
    };

    return {
      id: uuid(),
      message: messageText,
      type: 'action-response',
      threadId: threadId,
      data: {
        actionResult: actionResultArray,
        originatingRequestMessageId: originatingRequestMessageId,
        originatedActionMessageId: originatedActionMessageId,
        actionIndex: actionIndex,
      },
    };
  }

  const httpConfig = action.payload.http;
  const messageText = isSuccess
    ? httpConfig?.responseMessages?.success || 'Request completed successfully'
    : httpConfig?.responseMessages?.error || `Request failed`;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const returnType =
    httpConfig?.returnType || (httpConfig as any)?.projectModelName || null;

  // Safely extract the ID supporting both standard `id` and MongoDB `_id`, handling object and array formats
  let responseId = null;
  if (isSuccess && responseOrError) {
    if (Array.isArray(responseOrError) && responseOrError.length > 0) {
      responseId = responseOrError[0].id || responseOrError[0]._id || null;
    } else {
      responseId = responseOrError.id || responseOrError._id || null;
    }
  }

  const errorDetails = isSuccess
    ? null
    : responseOrError?.message || responseOrError || 'Unknown error';

  // Backend's EventParserService expects `data.actionResult` to be an array aligned by `actionIndex`
  actionResultArray[actionIndex] = {
    status: isSuccess ? 'success' : 'error',
    result: {
      Status: status,
      id: responseId,
      error: errorDetails,
    },
    entityName: returnType,
  };

  return {
    id: uuid(),
    message: messageText,
    type: 'action-response',
    // senderId: AI_CHAT_CONFIG.constants.aiReceiverId,
    threadId: threadId,
    data: {
      actionResult: actionResultArray,
      originatingRequestMessageId: originatingRequestMessageId,
      originatedActionMessageId: originatedActionMessageId,
      actionIndex: actionIndex,
    },
  };
}

/**
 * Standardized CRUD operation types mapped from AI intent.
 */
export type OperationType = 'CREATE' | 'READ' | 'UPDATE' | 'DELETE';

/**
 * Lightweight event payload tracking a database mutation or read event.
 */
export interface DataOperationEvent {
  id: string;
  timestamp: Date;
  operationType: OperationType;
  httpMethod?: string;
  endpointPath?: string;
  dataTableName?: string;
  title: string;
  description?: string;
  projectModelName: string;
  projectModelId?: string;
  recordIds: string[];
  isSuccess: boolean;
  errorMessage?: string;
  payloadData?: any; // Stores the request body payload to identify changed fields
}

/**
 * PURE FUNCTION: Evaluates the AI Action to determine the exact CRUD operation
 * and extracts the affected records to build a decoupled state event payload.
 * @param {AiAction} action - The AI action that was executed.
 * @param {boolean} isSuccess - Whether the API call succeeded.
 * @param {any} responseOrError - The raw HTTP body or error message.
 * @returns {DataOperationEvent} A strictly typed operation event.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function buildDataOperationEventPayload(
  action: AiAction,
  isSuccess: boolean,
  responseOrError: any,
): DataOperationEvent {
  // Strip the suffix to ensure the base operation logic always matches correctly
  const opStr = (action.operation || '')
    .toLowerCase()
    .replace('_onrapider', '');
  const httpMethod = (action.payload?.http?.method || '').toUpperCase();
  const rawPath = action.payload?.http?.path || '';
  const rawProxyPath = action.payload?.http?.proxyPath || '';
  const effectivePath = rawProxyPath || rawPath;
  const cleanLowerPath = effectivePath.split('?')[0].toLowerCase();

  let opType: OperationType = 'READ';
  if (httpMethod === 'GET' || httpMethod === 'HEAD') {
    opType = 'READ';
  } else if (httpMethod === 'DELETE') {
    opType = 'DELETE';
  } else if (httpMethod === 'PUT' || httpMethod === 'PATCH') {
    opType = 'UPDATE';
  } else if (httpMethod === 'POST') {
    const isReadOnlyPostPath =
      /\/(find|search|count|query)$/.test(cleanLowerPath) ||
      ['find', 'search', 'count', 'query', 'get', 'list'].includes(opStr);
    if (isReadOnlyPostPath) {
      opType = 'READ';
    } else if (opStr.includes('delete') || opStr.includes('remove')) {
      opType = 'DELETE';
    } else if (
      opStr.includes('update') ||
      opStr.includes('patch') ||
      opStr.includes('put')
    ) {
      opType = 'UPDATE';
    } else {
      opType = 'CREATE';
    }
  } else {
    if (
      opStr.includes('create') ||
      opStr.includes('post') ||
      opStr.includes('insert')
    )
      opType = 'CREATE';
    else if (
      opStr.includes('update') ||
      opStr.includes('patch') ||
      opStr.includes('put')
    )
      opType = 'UPDATE';
    else if (opStr.includes('delete') || opStr.includes('remove'))
      opType = 'DELETE';
  }

  let dataTableName: string | undefined;
  let projectModelId: string | undefined = (action.payload?.http as any)
    ?.projectModelId;

  const dmMatch =
    rawProxyPath.match(/\/project-data-manager\/([^/?#]+)/i) ||
    rawPath.match(/\/project-data-manager\/([^/?#]+)/i);
  if (dmMatch?.[1]) {
    dataTableName = dmMatch[1];
  }

  const pmMatch = rawPath.match(/\/project-models\/([^/?#]+)\/items/i);
  if (!projectModelId && pmMatch?.[1]) {
    projectModelId = pmMatch[1];
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const explicitReturnType =
    action.payload.http?.returnType ||
    (action.payload.http as any)?.projectModelName ||
    '';
  const isGenericReturnType =
    !explicitReturnType ||
    ['void', 'any', 'object', 'boolean', 'unknown entity'].includes(
      explicitReturnType.trim().toLowerCase(),
    );
  const returnType = !isGenericReturnType
    ? explicitReturnType
    : dataTableName || 'Unknown Entity';

  let recordIds: string[] = [];
  if (isSuccess && responseOrError && typeof responseOrError === 'object') {
    if (Array.isArray(responseOrError)) {
      // Support extracting both standard SQL 'id' and MongoDB '_id'
      recordIds = responseOrError
        .map((r) => r.id || r._id)
        .filter((id) => !!id);
    } else if (responseOrError.id || responseOrError._id) {
      recordIds = [responseOrError.id || responseOrError._id];
    }
  }

  // Fallback: If response doesn't contain ID (like 204 No Content), extract from request payload, params, or path
  if (recordIds.length === 0 && action.payload.http) {
    if (action.payload.http.body?.id || action.payload.http.body?._id) {
      recordIds = [action.payload.http.body.id || action.payload.http.body._id];
    } else if (
      action.payload.http.params?.id ||
      action.payload.http.params?._id
    ) {
      recordIds = [
        action.payload.http.params.id || action.payload.http.params._id,
      ];
    } else if (action.payload.http.path) {
      const pathSegments = action.payload.http.path
        .split('/')
        .filter((p) => !!p);
      const lastSegment = pathSegments[pathSegments.length - 1];
      // If it's an update, read, or delete, the last segment is usually the entity ID
      if (
        lastSegment &&
        (opType === 'UPDATE' || opType === 'DELETE' || opType === 'READ')
      ) {
        recordIds = [lastSegment];
      }
    }
  }

  return {
    id: generateTempId(),
    timestamp: new Date(),
    operationType: opType,
    httpMethod: httpMethod || undefined,
    endpointPath: effectivePath || undefined,
    dataTableName,
    title: action.description || 'Database Operation',
    projectModelName: returnType,
    projectModelId,
    recordIds,
    isSuccess,
    errorMessage: isSuccess
      ? undefined
      : responseOrError?.message || String(responseOrError),
    payloadData: action.payload.http?.body || action.payload.http?.params, // Keep a reference to the payload for diff highlighting
  };
}

/**
 * PURE FUNCTION: Appends URL encoded parameters to a given path.
 * Useful for GET endpoint actions that use 'params' instead of 'body'.
 * @param {string} path - The base URL path.
 * @param {any} params - An object containing query parameters.
 * @returns {string} The path with encoded query parameters appended.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function buildPathWithParams(path: string, params?: any): string {
  if (
    !params ||
    typeof params !== 'object' ||
    Object.keys(params).length === 0
  ) {
    return path;
  }

  const queryParts: string[] = [];
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null) {
      // Complex objects (like Loopback filters) need to be stringified before URL encoding
      if (
        key === 'filter' &&
        typeof params[key] === 'object' &&
        params[key]?.where &&
        typeof params[key]?.where === 'string'
      ) {
        try {
          const parsedWhere = JSON.parse(params[key].where);
          const stringifiedFilter = JSON.stringify({
            ...params[key],
            where: parsedWhere,
          });
          queryParts.push(
            `${encodeURIComponent(key)}=${encodeURIComponent(stringifiedFilter)}`,
          );
          continue;
        } catch (e) {
          // If parsing fails, fall back to encoding the original string
        }
      }
      const encodedValue =
        typeof value === 'object'
          ? encodeURIComponent(JSON.stringify(value))
          : encodeURIComponent(String(value));
      queryParts.push(`${encodeURIComponent(key)}=${encodedValue}`);
    }
  }

  const separator = path.includes('?') ? '&' : '?';
  return `${path}${separator}${queryParts.join('&')}`;
}

/**
 * PURE FUNCTION: Evaluates if an execution error matches the specific criteria
 * indicating the project server is not deployed or failed to deploy.
 * Detects 500-level errors originating from the admin-token auth endpoint.
 * @param {string | undefined} errorMsg - The error message string returned by the API.
 * @returns {boolean} True if the error indicates a server deployment is required.
 */
export function isDevAgentRequiredError(errorMsg?: string): boolean {
  if (!errorMsg) return false;
  const msg = errorMsg.toLowerCase();

  // Verify the error originates from the project token generation endpoint
  const isAuthTokenEndpoint = msg.includes(
    '/auth/login-to-project-by-admin-token',
  );

  // Verify it is a server-side failure
  const is5xxError =
    msg.includes('500') ||
    msg.includes('502') ||
    msg.includes('503') ||
    msg.includes('504');

  return isAuthTokenEndpoint && is5xxError;
}

/**
 * PURE FUNCTION: Determines if a file type can be natively previewed in a modern web browser.
 * Checks against common viewable MIME types and safely falls back to extension checking.
 * @param {string} [mimeType] - The MIME type of the file.
 * @param {string} [fileName] - The name of the file.
 * @param {string} [explicitExtension] - The explicit extension if provided by the backend.
 * @param {string} [url] - The file URL to parse as a final fallback.
 * @returns {boolean} True if the browser can likely render it, false otherwise.
 */
export function isBrowserPreviewable(
  mimeType?: string,
  fileName?: string,
  explicitExtension?: string,
  url?: string,
): boolean {
  // Sanitize inputs to handle literal 'undefined' or 'null' strings originating from the backend
  const cleanMime =
    mimeType && mimeType !== 'undefined' && mimeType !== 'null'
      ? mimeType.toLowerCase()
      : '';
  const cleanExt =
    explicitExtension &&
    explicitExtension !== 'undefined' &&
    explicitExtension !== 'null'
      ? explicitExtension.toLowerCase()
      : '';
  const cleanFileName =
    fileName && fileName !== 'undefined' && fileName !== 'null'
      ? fileName.toLowerCase()
      : '';
  const cleanUrl =
    url && url !== 'undefined' && url !== 'null' ? url.toLowerCase() : '';

  const previewableMimes = [
    'application/pdf',
    'text/plain',
    'text/html',
    'text/css',
    'application/json',
    'image/jpeg',
    'image/png',
    'image/gif',
    'image/webp',
    'image/svg+xml',
    'audio/mpeg',
    'audio/wav',
    'audio/ogg',
    'video/mp4',
    'video/webm',
    'video/ogg',
  ];

  // 1. Check sanitized Mime Type
  if (cleanMime && previewableMimes.includes(cleanMime)) {
    return true;
  }

  // 2. Fallback to extension check prioritizing: explicitExtension -> url -> fileName
  let ext = cleanExt;
  if (!ext && cleanUrl) {
    const urlPart = cleanUrl.split('?')[0];
    ext = urlPart.split('.').pop() || '';
  }
  if (!ext && cleanFileName) {
    ext = cleanFileName.split('.').pop() || '';
  }

  const previewableExts = [
    'pdf',
    'txt',
    'html',
    'htm',
    'jpg',
    'jpeg',
    'png',
    'gif',
    'webp',
    'svg',
    'mp3',
    'wav',
    'mp4',
    'webm',
    'json',
  ];

  return previewableExts.includes(ext);
}

/**
 * PURE FUNCTION: Forces a file download from a given URL by fetching it as a Blob.
 * This bypasses default browser behavior that might open S3/cross-origin files instead of downloading them.
 * Handles fetching the blob and generating a temporary object URL.
 * @param {string} url - The remote URL of the file to download.
 * @param {string} fileName - The desired name for the downloaded file.
 * @returns {Promise<void>} Resolves when the download is initiated.
 */
export async function forceDownloadFile(
  url: string,
  fileName: string,
): Promise<void> {
  try {
    const response = await fetch(url);
    if (!response.ok) throw new Error('Network response was not ok');
    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.style.display = 'none';
    a.href = blobUrl;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(blobUrl);
    document.body.removeChild(a);
  } catch (error) {
    console.error(
      'Download failed via fetch, attempting window open fallback',
      error,
    );
    // Fallback: simply open in new tab if fetch fails due to CORS, let the browser handle it
    window.open(url, '_blank');
  }
}
